import { Component, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { formatDateForInput } from '../../utils/date-utils';
import { UserProfileService } from '../../services/user-profile.service';
import {
  UserProfileExtended,
  LanguageExtended,
  SkillExtended,
  HighSchoolEducationExtended,
  UniversityEducationExtended,
  WorkExperienceExtended,
  ProjectExtended,
  DriversLicenses,
} from '../../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { AuthService } from '../../services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { SafeUrl } from '@angular/platform-browser';
import { AvatarService } from '../../services/avatar.service';
import { AvatarCropDialogComponent, AvatarCropDialogResult } from '../avatar-crop-dialog/avatar-crop-dialog.component';
import { ButtonComponent } from '../button/button.component';
import { ProfilePersonalInfoFormComponent } from '../profile-personal-info-form/profile-personal-info-form.component';
import { ProfilePersonalInfoPreviewComponent } from '../profile-personal-info-preview/profile-personal-info-preview.component';
import { ProfileFillMethod, RateLimitInfo, ProfileFillOptionsComponent } from '../job-apply/profile-fill-options/profile-fill-options.component';
import { ProfileSectionComponent } from '../profile-section/profile-section.component';
import { DriversLicenseSectionComponent } from '../drivers-license-section/drivers-license-section.component';
import { distinctUntilChanged, finalize, switchMap, tap } from 'rxjs/operators';
import { FullUserProfile } from '../../models/user.model';
import { SkeletonLoaderComponent } from '../shared/skeleton-loader/skeleton-loader.component';
import { AlertCardComponent } from '../alert-card/alert-card.component';
import { ToastService } from '../../services/toast.service';
import { CvOptionsService } from '../../services/cv-options.service';
import { CvOptions } from '../../models/cv-options.model';
import { CvParsingService, ParseCvPersonalInfo } from '../../../../../../libs/shared/src/lib/services/cv-parsing.service';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { ValidationService } from '../../../../../../libs/shared/src/lib/services/validation.service';
import { Address } from '../../../../../../libs/shared/src/lib/models/address.model';
import { getJobPositions } from '../../../../../../libs/shared/src/lib/i18n/job-positions/index';
import { ConsentResponse, ConsentType } from '../../../../../../libs/shared/src/lib/models/consent.model';
import { ConsentService } from '../../../../../../libs/shared/src/lib/services/consent.service';
import type { CountryCode } from 'libphonenumber-js';
import {
  DEFAULT_PHONE_COUNTRY,
  detectPhoneCountry,
  formatPhoneForInput,
  normalizePhoneForStorage,
} from '../../utils/phone-number.utils';

@Component({
  selector: 'app-profile-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    ButtonComponent,
    ProfileSectionComponent,
    DriversLicenseSectionComponent,
    ProfilePersonalInfoFormComponent,
    ProfilePersonalInfoPreviewComponent,
    ProfileFillOptionsComponent,
    SkeletonLoaderComponent,
    AlertCardComponent,
  ],
  templateUrl: './profile-form.component.html',
  styleUrls: ['./profile-form.component.scss'],
})
export class ProfileFormComponent implements OnInit, OnDestroy {
  @Input() userEmail: string = '';
  @Input() profileSettings: any = null;
  @Input() initialProfileData: any = null; // For restoring saved form state
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() profileDataChange = new EventEmitter<void>(); // Emitted when profile items change (for localStorage persistence)
  @ViewChild('personalInfoFormComponent') personalInfoFormComponent!: ProfilePersonalInfoFormComponent;

  // Define the personal info form in the parent component
  personalInfoForm!: FormGroup;
  resumeAndCoverForm!: FormGroup;

  // Track whether profile options should be shown
  showProfileOptions = false;
  selectedProfileFillMethod: ProfileFillMethod | null = null;
  resumeFile: File | null = null;
  resumeFileName = '';
  resumeFileSize = '';
  resumeLanguage = '';
  resumeUrl = '';
  aiParsingConsent = false;
  consents: ConsentResponse[] = [];
  consentUpdatingTypes = new Set<ConsentType>();
  readonly ConsentType = ConsentType;
  isEditMode = false;
  showProfileSections = false;
  isLoading = false;
  userProfile: UserProfileExtended | null = null;
  fullUserProfile: FullUserProfile | null = null;
  error: string | null = null;
  missingProfileFields: string[] = [];
  submissionError: string | null = null;

  // Store selected address from Google Places (synced from child component)
  selectedAddress: Address | null = null;

  // CV options data
  cvOptions: CvOptions | null = null;
  isLoadingCvOptions = false;
  educationLevelOptions: { value: number; label: string }[] = [];

  // AI Parsing properties
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  showAiParsingSection = false;
  isParsingInProgress = false;
  isFileUploaded = false;
  uploadedFile: File | null = null;
  filePreviewUrl: string | null = null;
  isDragging = false;
  parsingError: string | null = null;
  isParsingComplete = false;
  parsingSuccess = false;
  rateLimitInfo: RateLimitInfo | null = null;
  currentStep = 1;
  maxSteps = 5;

  // CV parsing loading state is now handled by isParsingInProgress

  // Autosave tracking
  hasPerformedAutosave = false;
  private autosaveInProgress = false;
  private parseWithAIInProgress = false;
  private lastParseAttemptTime = 0;

  // Profile completion method tracking
  profileCompletionMethod: 'ai' | 'manual' | null = null;
  showProfileSectionsForMethod = false;

  // Individual save tracking for AI-parsed items
  savingRows: { [key: string]: boolean } = {};
  rowErrors: { [key: string]: string | null } = {};

  // Subscriptions for cleanup
  private readonly subscriptions: Subscription[] = [];
  private consentControlsReady = false;
  private patchingConsentControls = false;
  private pendingAiParsingPreference: boolean | null = null;

  // Avatar (part of the personal-info section; editable regardless of edit mode)
  avatarFirstName = '';
  avatarLastName = '';
  avatarIdentifier = '';
  avatarPhoto: SafeUrl | string | null = null;
  isAvatarLoading = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly userProfileService: UserProfileService,
    private readonly toastService: ToastService,
    private readonly translocoService: TranslocoService,
    private readonly cvOptionsService: CvOptionsService,
    private readonly cvParsingService: CvParsingService,
    private readonly consentService: ConsentService,
    private readonly validationService: ValidationService,
    private readonly avatarService: AvatarService,
    private readonly dialog: MatDialog,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    // Initialize forms in constructor
    this.initForms();
  }

  ngOnInit(): void {
    this.setupFormListeners();
    this.setupConsentControlListeners();

    // Load CV options during initialization (works in both SSR and browser)
    this.loadCvOptions();

    // Job industry options are loaded in profile-personal-info-form component

    // Only load profile on client-side
    if (isPlatformBrowser(this.platformId)) {
      this.loadAvatar();
      // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        if (this.authService.isLoggedIn()) {
          // For logged-in users, load profile from server
          // Edit mode will be determined after profile loads based on missing required fields
          this.loadUserProfile();
          this.loadUserConsents();
        } else {
          // For non-logged-in users, initialize profile (empty or from localStorage)
          this.initializeEmptyProfile();

          // Check for missing required fields (same logic as logged-in users)
          // If initialProfileData was provided (restored from localStorage), check if profile is complete
          this.checkMissingProfileFields();
        }
      });
    }

    // Setup AI parsing subscriptions
    this.setupAiParsingSubscriptions();
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Initialize the avatar from the cached user: prefer the inline thumbnail,
   * otherwise download by photoID. Falls back to initials.
   */
  loadAvatar(): void {
    const user = this.authService.getCurrentUser();
    this.avatarFirstName = user?.firstName || '';
    this.avatarLastName = user?.lastName || '';
    this.avatarIdentifier = user?.email || String(user?.id ?? '');

    // Show the small thumbnail instantly as a placeholder...
    const thumbnail = this.avatarService.getThumbnailDataUrl(user);
    if (thumbnail) {
      this.avatarPhoto = thumbnail;
    }
    // ...then upgrade to the full-resolution image (the thumbnail is only ~50px
    // and looks blurry at the profile avatar size).
    if (user?.photoID) {
      if (!thumbnail) {
        this.isAvatarLoading = true;
      }
      this.avatarService.getAvatarUrl(user.photoID).subscribe({
        next: (url) => {
          if (url) {
            this.avatarPhoto = url;
          }
          this.isAvatarLoading = false;
        },
        error: () => {
          this.isAvatarLoading = false;
        },
      });
    }
  }

  openAvatarDialog(): void {
    const dialogRef = this.dialog.open(AvatarCropDialogComponent, {
      width: '460px',
      maxWidth: '95vw',
      autoFocus: false,
      panelClass: 'avatar-crop-dialog-panel',
    });

    dialogRef.afterClosed().subscribe((result?: AvatarCropDialogResult) => {
      if (result?.photoID) {
        this.isAvatarLoading = true;
        this.avatarService.getAvatarUrl(result.photoID).subscribe({
          next: (url) => {
            this.avatarPhoto = url;
            this.isAvatarLoading = false;
          },
          error: () => {
            this.isAvatarLoading = false;
          },
        });
      }
    });
  }

  removeAvatar(): void {
    this.isAvatarLoading = true;
    this.avatarService.clearAvatar().subscribe({
      next: () => {
        this.avatarPhoto = null;
        this.isAvatarLoading = false;
        this.toastService.success(
          this.translocoService.translate('pages.profile.avatar.dialog.removedTitle'),
          this.translocoService.translate('pages.profile.avatar.dialog.removedMessage')
        );
      },
      error: () => {
        this.isAvatarLoading = false;
        this.toastService.error(
          this.translocoService.translate('pages.profile.avatar.dialog.errorTitle'),
          this.translocoService.translate('pages.profile.avatar.dialog.uploadFailed')
        );
      },
    });
  }

  /**
   * Initialize empty profile structures for new users or job applications
   * Uses initialProfileData if provided (for restoring saved state)
   */
  private initializeEmptyProfile(): void {
    const initial = this.initialProfileData;
    this.aiParsingConsent = initial?.aiParsing || initial?.aiParsingConsent || false;

    this.userProfile = {
      languages: initial?.languages || [],
      skills: initial?.skills || [],
      highSchoolEducations: initial?.highSchoolEducations || [],
      universityEducations: initial?.universityEducations || [],
      workExperiences: initial?.workExperiences || [],
      projects: initial?.projects || [],
    };

    this.fullUserProfile = {
      firstName: initial?.firstName || '',
      lastName: initial?.lastName || '',
      gender: initial?.gender || '',
      email: initial?.email || '',
      language: initial?.language || '',
      telephone: initial?.telephone || '',
      birthDate: initial?.birthDate || '',
      title: initial?.title || '',
      titleAfterName: initial?.titleAfterName || '',
      aiMatching: initial?.aiMatching || false,
      aiParsing: initial?.aiParsing || initial?.aiParsingConsent || false,
      address: initial?.address || {
        streetName: '',
        streetNumber: undefined,
        city: '',
        administrativeArea: '',
        postalCode: undefined,
        country: '',
        formattedAddress: '',
        latitude: 0,
        longitude: 0,
      },
      languages: initial?.languages || [],
      skills: initial?.skills || [],
      highSchoolEducations: initial?.highSchoolEducations || [],
      universityEducations: initial?.universityEducations || [],
      workExperiences: initial?.workExperiences || [],
      projects: initial?.projects || [],
      candidateAttachment: initial?.candidateAttachment || [],
    };

    // If we have initial data, also patch the personal info form
    if (initial) {
      const phoneCountry = detectPhoneCountry(initial.telephone, DEFAULT_PHONE_COUNTRY);
      this.personalInfoForm?.patchValue({
        firstName: initial.firstName || '',
        lastName: initial.lastName || '',
        gender: initial.gender || '',
        phoneCountry,
        phone: formatPhoneForInput(initial.telephone || '', phoneCountry),
        dateOfBirth: initial.birthDate || '',
        streetName: initial.address?.streetName || '',
        streetNumber: initial.address?.streetNumber || '',
        city: initial.address?.city || '',
        zipCode: initial.address?.postalCode || '',
        country: initial.address?.country || '',
        title: initial.title || '',
        titleAfterName: initial.titleAfterName || '',
        aiMatching: initial.aiMatching || false,
        aiParsing: initial.aiParsing || initial.aiParsingConsent || false,
      });
    }
  }

  /**
   * Setup AI parsing service subscriptions
   */
  setupAiParsingSubscriptions(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Subscribe to parsing progress
      this.subscriptions.push(
        this.cvParsingService.parsingInProgress$.subscribe((inProgress) => {
          this.isParsingInProgress = inProgress;
          if (inProgress) {
            this.currentStep = 1;
            this.parsingError = null;
          }
        })
      );

      // Subscribe to parsing success
      this.subscriptions.push(
        this.cvParsingService.parsingSuccess$.subscribe((success) => {
          if (success) {
            this.parsingSuccess = true;
            this.isParsingComplete = true;
            this.currentStep = this.maxSteps;

            // Show profile sections if AI method was chosen
            if (this.profileCompletionMethod === 'ai') {
              this.showProfileSectionsForMethod = true;
            }

            // Preserve uploaded file information in form so user can see which file was processed
            // The file should remain visible in the UI even after successful parsing
            if (this.uploadedFile && this.resumeAndCoverForm.get('resumeFile')?.value === null) {
              this.resumeAndCoverForm.patchValue({
                resumeFile: this.uploadedFile,
              });
            }

            this.toastService.success(
              this.translocoService.translate('profile.aiParsing.notifications.parseSuccess'),
              this.translocoService.translate('profile.aiParsing.notifications.parseSuccessDesc')
            );
            // Note: loadAutofillData() is called by resumeDataParsed$ subscription to avoid duplicates
          }
        })
      );

      // Subscribe to parsing errors
      this.subscriptions.push(
        this.cvParsingService.parsingError$.subscribe((error) => {
          if (error) {
            this.parsingError = error;
            this.isParsingComplete = false;
            this.parsingSuccess = false;
            this.toastService.error(this.translocoService.translate('profile.aiParsing.notifications.parseError'), error);
          }
        })
      );

      // Subscribe to resume data parsed events - this is the main trigger for loading autofilled data
      this.subscriptions.push(
        this.cvParsingService.resumeDataParsed$.subscribe(() => {
          this.loadAutofillData();
        })
      );
    }
  }

  /**
   * Load CV options from the service
   * This works in both SSR and browser environments
   */
  loadCvOptions(): void {
    this.isLoadingCvOptions = true;

    this.cvOptionsService
      .getCvOptions()
      .pipe(finalize(() => (this.isLoadingCvOptions = false)))
      .subscribe({
        next: (response: any) => {
          if (response && response.body) {
            this.cvOptions = response.body;
            this.educationLevelOptions = (response.body.educationLevels || [])
              .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
              .map((level: any) => ({
                value: level.id,
                label: this.translocoService.translate('jobEducationLevels.' + level.educationKey)
              }));
          }
        },
        error: (error: any) => {
          console.error('Error loading CV options:', error);
        },
      });
  }

  getCurrentUser() {
    return this.authService.getCurrentUser();
  }

  isUserAuthenticated(): boolean {
    return this.authService.isLoggedIn();
  }

  get effectiveAiParsingConsent(): boolean {
    return !!(
      this.aiParsingConsent ||
      this.personalInfoForm?.get('aiParsing')?.value ||
      this.fullUserProfile?.aiParsing ||
      this.consentService.hasConsent(ConsentType.AI_PARSING)
    );
  }

  /**
   * Load user profile data from the service
   */
  loadUserProfile(): void {
    // Skip during SSR
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Skip if user is not logged in
    if (!this.authService.isLoggedIn()) {
      return;
    }

    if (!this.getCurrentUser()) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    this.userProfileService
      .getUserProfile()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (profile) => {
          if (profile) {
            this.userProfile = profile;
            this.fullUserProfile = {
              ...profile,
              firstName: this.getCurrentUser()?.firstName || '',
              lastName: this.getCurrentUser()?.lastName || '',
              email: this.getCurrentUser()?.email || '',
              language: this.getCurrentUser()?.language || '',
              aiMatching: this.getCurrentUser()?.aiMatching || false,
              aiParsing: this.getCurrentUser()?.aiParsing || this.consentService.hasConsent(ConsentType.AI_PARSING),
            };
            this.prefillFormWithProfileData(this.fullUserProfile);

            // Check for missing required fields after loading profile
            this.checkMissingProfileFields();
          }
        },
        error: (err) => {
          this.error = err.message || 'Failed to load user profile';
          console.error('Error loading user profile:', err);
        },
      });
  }

  get userHasLongCV() {
    return this.missingProfileFields.length === 0;
  }

  get longCVHasAtLeastOneSection() {
    if (!this.fullUserProfile) return false;

    // Check if user has any existing CV data
    const hasLanguages = (this.fullUserProfile?.languages?.length ?? 0) > 0;
    const hasSkills = (this.fullUserProfile?.skills?.length ?? 0) > 0;
    const hasWorkExperience = (this.fullUserProfile?.workExperiences?.length ?? 0) > 0;
    const hasProjects = (this.fullUserProfile?.projects?.length ?? 0) > 0;
    const hasHighSchool = (this.fullUserProfile?.highSchoolEducations?.length ?? 0) > 0;
    const hasUniversity = (this.fullUserProfile?.universityEducations?.length ?? 0) > 0;

    return hasLanguages || hasSkills || hasWorkExperience || hasProjects || hasHighSchool || hasUniversity;
  }

  /**
   * Check if user has existing profile data that would be overwritten by AI parsing
   * This is used to show a confirmation checkbox before allowing AI parsing
   */
  get hasExistingProfileData(): boolean {
    return this.longCVHasAtLeastOneSection;
  }

  /**
   * Prefill the form with user profile data
   */
  prefillFormWithProfileData(profile: FullUserProfile): void {
    if (!profile) return;

    // Use the database gender values directly
    // The form options now use 'db.male', 'db.female', 'db.other'
    let genderValue = profile.gender || '';

    // If the gender value doesn't have the 'db.' prefix, add it (for backward compatibility)
    if (genderValue === 'male') genderValue = 'db.male';
    else if (genderValue === 'female') genderValue = 'db.female';
    else if (genderValue === 'other') genderValue = 'db.other';

    // Format birth date for the date input
    // Format date of birth using centralized utility function
    let formattedDateOfBirth = '';
    if (profile['birthDate']) {
      formattedDateOfBirth = formatDateForInput(profile['birthDate']);
    }

    // Extract keys from new backend format (objects with id and key fields)
    let jobTypeArr: string[] = [];
    let jobCategoryArr: string[] = [];
    let jobPositionArr: string[] = [];
    let selfDescriptionArr: string[] = [];

    try {
      // Use new employmentTypes field from backend
      if (profile.employmentTypes && Array.isArray(profile.employmentTypes)) {
        jobTypeArr = profile.employmentTypes.map((et: any) => et.employmentTypeKey);
      }

      // Use new jobCategories field from backend
      if (profile.jobCategories && Array.isArray(profile.jobCategories)) {
        jobCategoryArr = profile.jobCategories.map((jc: any) => jc.categoryKey);
      }

      // Use new jobPositions field from backend
      if (profile.jobPositions && Array.isArray(profile.jobPositions)) {
        jobPositionArr = profile.jobPositions.map((jp: any) => jp.positionKey);
      }

      // Handle selfDescription (still comes as string or string array)
      const selfDescriptionField = profile.selfDescription;
      if (selfDescriptionField && selfDescriptionField !== '[]') {
        selfDescriptionArr = typeof selfDescriptionField === 'string' ? JSON.parse(selfDescriptionField) : selfDescriptionField;
        if (!Array.isArray(selfDescriptionArr)) selfDescriptionArr = [];
      }
    } catch (error) {
      console.error('Error parsing array fields:', error);
      // Reset to empty arrays on parse error
      jobTypeArr = [];
      jobCategoryArr = [];
      jobPositionArr = [];
      selfDescriptionArr = [];
    }

    const phoneCountry = detectPhoneCountry(profile.telephone, DEFAULT_PHONE_COUNTRY);
    const aiMatchingConsent = this.consents.length
      ? this.getConsentGranted(this.consents, ConsentType.AI_MATCHING, profile.aiMatching || false)
      : profile.aiMatching || false;
    const aiParsingConsent = this.consents.length
      ? this.getConsentGranted(this.consents, ConsentType.AI_PARSING, this.aiParsingConsent)
      : this.aiParsingConsent;

    // Patch form with profile data
    this.patchingConsentControls = true;
    this.personalInfoForm.patchValue({
      title: profile.title || '',
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      titleAfterName: profile.titleAfterName || '',
      email: profile.email || '',
      phoneCountry,
      phone: formatPhoneForInput(profile.telephone || '', phoneCountry),
      gender: genderValue,
      dateOfBirth: formattedDateOfBirth,
      streetName: profile.address?.streetName || '',
      streetNumber: profile.address?.streetNumber || '',
      city: profile.address?.city || '',
      zipCode: profile.address?.postalCode || '',
      country: profile.address?.country || '',
      aiMatching: aiMatchingConsent,
      aiParsing: aiParsingConsent,
      educationLevelID: profile.educationLevelID || '',
      jobType: jobTypeArr,
      jobCategory: jobCategoryArr,
      jobPosition: jobPositionArr,
      selfDescription: selfDescriptionArr,
    });
    this.patchingConsentControls = false;
    this.aiParsingConsent = aiParsingConsent;

    // Set the selectedAddress in parent (and child if available) so lat/lng are preserved
    // Preserve existing lat/lng if profile doesn't have valid ones (e.g., from Google Places)
    if (profile.address) {
      const existingLat = this.selectedAddress?.latitude;
      const existingLng = this.selectedAddress?.longitude;
      const profileHasLatLng = profile.address.latitude && profile.address.longitude;

      const addressData: Address = {
        formattedAddress: profile.address.formattedAddress || '',
        streetName: profile.address.streetName,
        streetNumber: profile.address.streetNumber,
        city: profile.address.city,
        administrativeArea: profile.address.administrativeArea,
        postalCode: profile.address.postalCode,
        country: profile.address.country,
        // Prefer profile lat/lng if valid, otherwise keep existing (from Google Places)
        latitude: profileHasLatLng ? profile.address.latitude : existingLat || 0,
        longitude: profileHasLatLng ? profile.address.longitude : existingLng || 0,
      };
      // Store in parent for reliable access
      this.selectedAddress = addressData;
      // Also set in child if available
      if (this.personalInfoFormComponent) {
        this.personalInfoFormComponent.selectedAddress = addressData;
      }
    }
  }

  // Listen for changes to resumeLanguage for conditional display of profile options
  setupFormListeners(): void {
    this.resumeAndCoverForm.get('resumeLanguage')?.valueChanges.subscribe(() => {
      this.checkShowProfileOptions();
    });
  }

  private setupConsentControlListeners(): void {
    const aiParsingControl = this.personalInfoForm.get('aiParsing');
    if (!aiParsingControl) {
      return;
    }

    this.subscriptions.push(
      aiParsingControl.valueChanges.pipe(distinctUntilChanged()).subscribe((granted: boolean) => {
        if (!this.patchingConsentControls) {
          this.aiParsingConsent = !!granted;
        }
      })
    );
  }

  private loadUserConsents(): void {
    if (!isPlatformBrowser(this.platformId) || !this.authService.isLoggedIn()) {
      return;
    }

    this.consentService.getUserConsents(true).subscribe({
      next: (consents: ConsentResponse[]) => {
        this.consents = consents;
        this.patchConsentControlsFromConsents(consents);
        this.consentControlsReady = true;
        if (this.pendingAiParsingPreference !== null) {
          const pendingPreference = this.pendingAiParsingPreference;
          this.pendingAiParsingPreference = null;
          this.onAiParsingConsentPreferenceChange(pendingPreference);
        }
      },
      error: (error: any) => {
        console.error('Failed to load user consents:', error);
        this.consentControlsReady = true;
      },
    });
  }

  private patchConsentControlsFromConsents(consents: ConsentResponse[]): void {
    const aiMatching = this.getConsentGranted(consents, ConsentType.AI_MATCHING, this.fullUserProfile?.aiMatching ?? false);
    const aiParsing = this.getConsentGranted(consents, ConsentType.AI_PARSING, this.consentService.hasConsent(ConsentType.AI_PARSING));

    this.patchingConsentControls = true;
    this.personalInfoForm.patchValue(
      {
        aiMatching,
        aiParsing,
      },
      { emitEvent: false }
    );
    this.patchingConsentControls = false;

    this.aiParsingConsent = aiParsing;
    if (this.fullUserProfile) {
      this.fullUserProfile.aiMatching = aiMatching;
      this.fullUserProfile.aiParsing = aiParsing;
    }
  }

  private getConsentGranted(consents: ConsentResponse[], consentType: ConsentType, fallback = false): boolean {
    const consent = consents.find((item) => item.consentType === consentType);
    return consent ? consent.granted === true && !consent.withdrawnAt : fallback;
  }

  private persistConsentPreference(consentType: ConsentType, granted: boolean): Observable<ConsentResponse> {
    const currentConsent = this.consents.find((consent) => consent.consentType === consentType);

    this.consentUpdatingTypes.add(consentType);

    return this.consentService.setUserConsent(this.consents, consentType, granted, {
      jobsRequest: true,
      language: this.getConsentLanguage(),
      version: currentConsent?.version ?? '1.0',
      purposeDescription: this.translocoService.translate('descriptions.' + consentType),
    })
      .pipe(
        tap((updatedConsent: ConsentResponse) => this.applyConsentUpdate(updatedConsent)),
        finalize(() => this.consentUpdatingTypes.delete(consentType))
      );
  }

  private applyConsentUpdate(updatedConsent: ConsentResponse): void {
    const index = this.consents.findIndex((consent) => consent.consentType === updatedConsent.consentType);
    if (index !== -1) {
      this.consents[index] = updatedConsent;
    } else {
      this.consents.push(updatedConsent);
    }

    const granted = updatedConsent.granted === true && !updatedConsent.withdrawnAt;

    if (updatedConsent.consentType === ConsentType.AI_MATCHING && this.fullUserProfile) {
      this.fullUserProfile.aiMatching = granted;
    }

    if (updatedConsent.consentType === ConsentType.AI_PARSING) {
      this.aiParsingConsent = granted;
      this.personalInfoForm.get('aiParsing')?.setValue(granted, { emitEvent: false });
      if (this.fullUserProfile) {
        this.fullUserProfile.aiParsing = granted;
      }
    }
  }

  private saveProfileConsentPreferences(aiMatching: boolean, aiParsing: boolean): Observable<ConsentResponse[]> {
    const updates: Observable<ConsentResponse>[] = [];

    if (this.getConsentGranted(this.consents, ConsentType.AI_MATCHING, this.fullUserProfile?.aiMatching ?? false) !== aiMatching) {
      updates.push(this.persistConsentPreference(ConsentType.AI_MATCHING, aiMatching));
    }

    if (this.getConsentGranted(this.consents, ConsentType.AI_PARSING, this.fullUserProfile?.aiParsing ?? false) !== aiParsing) {
      updates.push(this.persistConsentPreference(ConsentType.AI_PARSING, aiParsing));
    }

    return updates.length ? forkJoin(updates) : of([]);
  }

  onAiParsingConsentPreferenceChange(granted: boolean): void {
    this.aiParsingConsent = granted;
    this.personalInfoForm.get('aiParsing')?.setValue(granted, { emitEvent: false });

    if (!this.authService.isLoggedIn() || !this.consentControlsReady) {
      if (this.authService.isLoggedIn()) {
        this.pendingAiParsingPreference = granted;
      }
      return;
    }

    if (this.getConsentGranted(this.consents, ConsentType.AI_PARSING, false) === granted) {
      return;
    }

    this.persistConsentPreference(ConsentType.AI_PARSING, granted).subscribe({
      error: (error: any) => {
        console.error('Failed to update AI parsing preference:', error);
        const previousGranted = this.getConsentGranted(this.consents, ConsentType.AI_PARSING, false);
        this.aiParsingConsent = previousGranted;
        this.personalInfoForm.get('aiParsing')?.setValue(previousGranted, { emitEvent: false });
        this.toastService.error(
          this.translocoService.translate('profile.toast.updateErrorTitle'),
          this.translocoService.translate('profile.toast.updateError'),
          true,
          5000
        );
      },
    });
  }

  private getConsentLanguage(): string {
    const lang = this.translocoService.getActiveLang().toUpperCase();
    return ['EN', 'SK', 'CZ'].includes(lang) ? lang : 'EN';
  }

  initForms(): void {
    // Personal info form - no email field (now read-only display only)
    this.personalInfoForm = this.fb.group({
      title: [''],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      titleAfterName: [''],
      gender: ['', Validators.required],
      phoneCountry: [DEFAULT_PHONE_COUNTRY, Validators.required],
      phone: ['', Validators.required],
      dateOfBirth: ['', [Validators.required, this.dateInPastValidator]],
      // address
      streetName: ['', Validators.required],
      streetNumber: ['', Validators.required],
      city: ['', Validators.required],
      zipCode: ['', Validators.required],
      country: ['', Validators.required],
      aiMatching: [false],
      aiParsing: [false],
      educationLevelID: [''],
      // New array fields
      jobType: [[]],
      jobCategory: [[]],
      jobPosition: [[]],
      selfDescription: [[]],
    });

    // Resume form
    this.resumeAndCoverForm = this.fb.group({
      resumeFile: [null, Validators.required],
      resumeLanguage: ['', Validators.required],
      attachmentId: [''],
      resumeUrl: [''],
    });
  }

  /**
   * Custom validator to ensure date of birth is in the past
   */
  dateInPastValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const inputDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (inputDate >= today) {
      return { dateInPast: true };
    }

    return null;
  }

  // Check if both resume file and language are selected to show profile options
  checkShowProfileOptions(): void {
    const resumeFile = this.resumeAndCoverForm.get('resumeFile')?.value;
    const resumeLanguage = this.resumeAndCoverForm.get('resumeLanguage')?.value;

    this.showProfileOptions = !!(resumeFile && resumeLanguage);

    // Reset selected method if no longer showing options
    if (!this.showProfileOptions) {
      this.selectedProfileFillMethod = null;
    }
  }

  // Handle profile fill method selection
  onProfileFillMethodSelected(method: ProfileFillMethod): void {
    this.selectedProfileFillMethod = method;

    if (method === ProfileFillMethod.MANUAL_ENTRY) {
      // Validate before allowing manual entry
      if (!this.validateAndShowErrorIfNeeded()) {
        return;
      }

      // All validation passed - For manual entry, show sections and hide AI parsing
      this.showProfileSections = true;
      this.profileCompletionMethod = 'manual';
      this.showProfileSectionsForMethod = true;
      this.hideAiParsing();
      this.isParsingComplete = false;
      this.parsingSuccess = false;
      this.resetFileUpload();
    } else {
      // For AI parsing, just set the flag
      this.showProfileSections = true;
    }
  }

  /**
   * Handle file processed event from profile-fill-options component
   */
  onFileProcessed(data: { file: File | null; language: string; attachmentId?: string; resumeUrl?: string; aiParsingConsent: boolean }): void {
    if (data.file) {
      // Store the file data for potential AI parsing
      this.resumeFile = data.file;
      this.resumeFileName = data.file.name;
      this.resumeFileSize = this.formatFileSize(data.file.size);
      this.resumeLanguage = data.language;
      this.resumeUrl = data.resumeUrl || '';
      this.aiParsingConsent = data.aiParsingConsent;
      if (data.aiParsingConsent && this.personalInfoForm.get('aiParsing')?.value !== true) {
        this.personalInfoForm.get('aiParsing')?.setValue(true);
      }

      // Also store in uploadedFile for AI parsing
      this.uploadedFile = data.file;
      this.isFileUploaded = true;

      // Update the resume form with the file data
      this.resumeAndCoverForm.patchValue({
        resumeFile: data.file,
        resumeLanguage: data.language,
      });

      // If attachment ID is provided, store it
      if (data.attachmentId) {
        this.resumeAndCoverForm.patchValue({
          attachmentId: data.attachmentId,
        });
      }

      // If resume URL is provided, store it
      if (data.resumeUrl) {
        this.resumeAndCoverForm.patchValue({
          resumeUrl: data.resumeUrl,
        });
      }

      // Check if we should show profile options
      this.checkShowProfileOptions();

      // Trigger AI parsing when file is processed as part of AI autofill selection
      // Add extra protection against multiple calls
      if (!this.isParsingInProgress && !this.parseWithAIInProgress) {
        this.parseWithAI();
      }
    } else {
      // Clear file data if null file is passed
      this.resumeFile = null;
      this.resumeFileName = '';
      this.resumeFileSize = '';
      this.resumeLanguage = '';
      this.resumeUrl = '';
      this.uploadedFile = null;
      this.isFileUploaded = false;
      this.resumeAndCoverForm.patchValue({
        resumeFile: null,
        resumeLanguage: data.language,
      });
      this.checkShowProfileOptions();
    }
  }

  // Removed onPersonalInfoFormSubmit as we're using the form directly

  /**
   * Validate that all selected job positions belong to selected categories
   * Remove any positions that don't match the selected categories
   */
  private validateJobPositions(selectedCategories: string[], selectedPositions: string[]): string[] {
    if (!selectedPositions || selectedPositions.length === 0) {
      return [];
    }

    if (!selectedCategories || selectedCategories.length === 0) {
      // No categories selected, so no positions should be selected
      return [];
    }

    // Get the mapping from the child component
    const childComponent = this.personalInfoFormComponent;
    if (!childComponent || !childComponent.allPositions || !childComponent.categoryKeyToIdMap) {
      // Can't validate, return as-is
      return selectedPositions;
    }

    // Convert selected category keys to IDs
    const selectedCategoryIds = selectedCategories.map((key) => childComponent.categoryKeyToIdMap.get(key)).filter((id) => id !== undefined) as number[];

    // Get valid position keys (positions that belong to selected categories)
    const validPositionKeys = childComponent.allPositions.filter((pos) => selectedCategoryIds.includes(pos.jobCategoryId)).map((pos) => pos.positionKey);

    // Filter selected positions to only include valid ones
    const validatedPositions = selectedPositions.filter((posKey) => validPositionKeys.includes(posKey));

    // Log warning if positions were removed
    if (validatedPositions.length !== selectedPositions.length) {
      console.warn(
        'Removed invalid job positions before submit:',
        selectedPositions.filter((p) => !validatedPositions.includes(p))
      );
    }

    return validatedPositions;
  }

  submitForm(): void {
    // Validate form fields and update missingProfileFields for the alert
    this.validateFormAndUpdateMissingFields();

    // If there are missing fields, don't proceed with submission
    if (this.missingProfileFields.length > 0) {
      // Mark all form controls as touched to show validation errors
      this.markFormGroupTouched(this.personalInfoForm);
      return;
    }

    // Prepare form data
    const formData = {
      ...this.personalInfoForm.value,
      ...this.resumeAndCoverForm.value,
      profileFillMethod: this.selectedProfileFillMethod,
    };
    formData.phone = this.normalizePhoneValue(formData.phone, formData.phoneCountry);

    // Still emit the form data for any parent components that might be listening
    this.formSubmit.emit(formData);

    // Only update personal info if user is logged in
    if (!this.authService.isLoggedIn()) {
      // Update fullUserProfile with form data so preview mode can display it
      if (this.fullUserProfile) {
        this.fullUserProfile.firstName = formData.firstName;
        this.fullUserProfile.lastName = formData.lastName;
        this.fullUserProfile.gender = formData.gender;
        this.fullUserProfile.email = formData.email;
        this.fullUserProfile.telephone = formData.phone;
        this.fullUserProfile.birthDate = formData.dateOfBirth;
        this.fullUserProfile.address = {
          streetName: formData.streetName || '',
          streetNumber: formData.streetNumber || '',
          city: formData.city || '',
          administrativeArea: this.selectedAddress?.administrativeArea || this.fullUserProfile.address?.administrativeArea || '',
          postalCode: formData.zipCode || '',
          country: formData.country || '',
          formattedAddress: this.selectedAddress?.formattedAddress || this.fullUserProfile.address?.formattedAddress || '',
          latitude: this.selectedAddress?.latitude || this.fullUserProfile.address?.latitude || 0,
          longitude: this.selectedAddress?.longitude || this.fullUserProfile.address?.longitude || 0,
        };
        this.fullUserProfile.title = formData.title;
        this.fullUserProfile.titleAfterName = formData.titleAfterName;
        this.fullUserProfile.aiMatching = formData.aiMatching;
        this.fullUserProfile.aiParsing = formData.aiParsing;
      }

      this.toastService.success(
        this.translocoService.translate('profile.toast.updateSuccess'),
        this.translocoService.translate('profile.toast.savedLocally'),
        true,
        3000
      );

      // Switch to preview mode after successful local save (same UX as logged-in users)
      this.isEditMode = false;
      return;
    }

    const longcvID = this.authService.getCurrentUser()?.['longcvID'];
    if (!longcvID) {
      console.error('User does not have a CV ID');
      return;
    }

    // Use selectedAddress stored in parent (synced from child via addressChange event)
    const selectedAddress = this.selectedAddress;

    // Build address from form fields, using selectedAddress only for metadata (lat/lng)
    let address = undefined;
    if (formData.streetName || formData.city) {
      // Always use form values for editable fields, but preserve lat/lng from Google Places if available
      address = {
        formattedAddress: `${formData.streetName || ''} ${formData.streetNumber || ''}, ${formData.city || ''} ${formData.zipCode || ''}, ${
          formData.country || ''
        }`.trim(),
        streetName: formData.streetName,
        streetNumber: formData.streetNumber || undefined,
        city: formData.city,
        administrativeArea: selectedAddress?.administrativeArea || this.fullUserProfile?.address?.administrativeArea,
        postalCode: formData.zipCode || undefined,
        country: formData.country,
        latitude: selectedAddress?.latitude || this.fullUserProfile?.address?.latitude || 0,
        longitude: selectedAddress?.longitude || this.fullUserProfile?.address?.longitude || 0,
      };
    }

    // Validate and clean job positions before submitting
    const validatedJobPositions = this.validateJobPositions(formData.jobCategory || [], formData.jobPosition || []);

    // Extract the longCV data from the form data
    const longCV = {
      id: longcvID,
      gender: formData.gender,
      title: formData.title,
      titleAfterName: formData.titleAfterName,
      birthDate: formData.dateOfBirth,
      telephone: formData.phone,
      educationLevelID: formData.educationLevelID || null,
      jobType: formData.jobType || [],
      jobCategory: formData.jobCategory || [],
      jobPosition: validatedJobPositions,
      selfDescription: formData.selfDescription || [],
      address: address,
    };

    const aiMatching = !!this.personalInfoForm.get('aiMatching')?.value;
    const aiParsing = !!this.personalInfoForm.get('aiParsing')?.value;

    // Save staged consent changes first so the legacy personal-info endpoint
    // sees the same AI matching state without racing consent creation.
    this.saveProfileConsentPreferences(aiMatching, aiParsing)
      .pipe(switchMap(() => this.userProfileService.updatePersonalInfo(longCV, aiMatching)))
      .subscribe({
        next: (_response: any) => {
          // Show success toast notification with translations
          this.toastService.success(
            this.translocoService.translate('profile.toast.updateSuccess'),
            this.translocoService.translate('profile.toast.personalInfoSaved'),
            true,
            5000
          );

          // Switch to preview mode after successful submission
          this.isEditMode = false;

          // Get updated user data from the server
          this.authService.getCurrentUserBE().subscribe({
            next: (updatedUser) => {
              // Update the current user in the auth service
              this.authService.currentUserValue = updatedUser;
              this.authService.userFromBE = updatedUser;

              // Update localStorage with the new user data
              if (isPlatformBrowser(this.platformId)) {
                localStorage.setItem('user', JSON.stringify(updatedUser));
                this.consents.forEach((consent) => this.consentService.updateUserConsentsInStorage(consent));
              }

              // Reload the profile data to reflect the changes
              this.loadUserProfile();
              this.loadUserConsents();
            },
            error: (error) => {
              console.error('Error getting updated user data:', error);
              // Still try to load profile even if getting updated user fails
              this.loadUserProfile();
              this.loadUserConsents();
            },
          });
        },
        error: (error: any) => {
          console.error('Error updating profile:', error);
          // Set submission error message for the alert card
          this.submissionError = this.translocoService.translate('profile.toast.updateError');
          // Show error toast notification
          this.toastService.error(
            this.translocoService.translate('profile.toast.updateErrorTitle'),
            this.translocoService.translate('profile.toast.updateError'),
            true,
            5000
          );
        },
      });
  }

  // Helper method to mark all controls in a form group as touched
  markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      // If this is a nested form group, mark its controls as touched too
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private normalizePhoneValue(value: string | null | undefined, country: CountryCode = DEFAULT_PHONE_COUNTRY): string {
    return normalizePhoneForStorage(value, country || DEFAULT_PHONE_COUNTRY);
  }

  /**
   * Handle address change from child component
   * Stores the address with lat/lng so it's available even if child is destroyed
   */
  onAddressChange(address: Address): void {
    this.selectedAddress = address;
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    if (!this.fullUserProfile) return;
    this.prefillFormWithProfileData(this.fullUserProfile);
  }

  getGenderLabel(value: string): string {
    const genderMap: { [key: string]: string } = {
      'db.male': 'jobApply.personal.male',
      'db.female': 'jobApply.personal.female',
      'db.other': 'jobApply.personal.other',
      // For backward compatibility
      male: 'jobApply.personal.male',
      female: 'jobApply.personal.female',
      other: 'jobApply.personal.other',
    };
    return genderMap[value] || '';
  }

  /**
   * Unified handler for profile item operations (add, edit, delete, save, batchSave)
   * Processes events from the profile-section component and handles all CRUD operations
   */
  onProfileItemAction(event: {
    itemType: 'languages' | 'skills' | 'highSchool' | 'university' | 'workExperience' | 'projects';
    action: 'add' | 'edit' | 'delete' | 'save' | 'batchSave';
    item?: LanguageExtended | SkillExtended | HighSchoolEducationExtended | UniversityEducationExtended | WorkExperienceExtended | ProjectExtended;
    items?: (LanguageExtended | SkillExtended | HighSchoolEducationExtended | UniversityEducationExtended | WorkExperienceExtended | ProjectExtended)[];
    index?: number;
    isAutoSave?: boolean;
  }): void {
    if (!this.fullUserProfile) return;

    const { itemType, action, item, items, index, isAutoSave } = event;

    // Handle batch save action for AI-parsed items
    if (action === 'batchSave' && items) {
      this.saveBatchItems(itemType, items);
      return;
    }

    // Handle individual save action for AI-parsed items
    if (action === 'save') {
      this.saveIndividualItem(itemType, item, index);
      return;
    }

    // Handle other actions (add, edit, delete) as before
    // Get the current items array based on type
    let currentItems;
    if (itemType === 'languages') {
      currentItems = [...(this.fullUserProfile.languages || [])];
    } else if (itemType === 'skills') {
      currentItems = [...(this.fullUserProfile.skills || [])];
    } else if (itemType === 'highSchool') {
      currentItems = [...(this.fullUserProfile.highSchoolEducations || [])];
    } else if (itemType === 'university') {
      currentItems = [...(this.fullUserProfile.universityEducations || [])];
    } else if (itemType === 'workExperience') {
      currentItems = [...(this.fullUserProfile.workExperiences || [])];
    } else if (itemType === 'projects') {
      currentItems = [...(this.fullUserProfile.projects || [])];
    } else {
      return;
    }

    // Perform the requested action
    let updatedItems;
    let filteredItemsForBackend; // Separate array for backend requests

    switch (action) {
      case 'add':
        if (!item) return;

        // Check for duplicate language
        if (itemType === 'languages') {
          const newLang = item as LanguageExtended;
          const isDuplicate = currentItems.some((lang) => (lang as LanguageExtended).language === newLang.language);

          if (isDuplicate) {
            this.toastService.error(this.translocoService.translate('common.error'), this.translocoService.translate('profile.languages.duplicateError'));
            return;
          }
        }

        // Check for duplicate skill
        if (itemType === 'skills') {
          const newSkill = item as SkillExtended;
          const isDuplicate = currentItems.some((skill) => (skill as SkillExtended).skillName === newSkill.skillName);

          if (isDuplicate) {
            this.toastService.error(this.translocoService.translate('common.error'), this.translocoService.translate('profile.skills.duplicateError'));
            return;
          }
        }

        updatedItems = [...currentItems, item];
        filteredItemsForBackend = updatedItems;
        break;
      case 'edit':
        if (!item || index === undefined) return;

        // Check for duplicate language (excluding current item being edited)
        if (itemType === 'languages') {
          const updatedLang = item as LanguageExtended;
          const isDuplicate = currentItems.some((lang, i) => i !== index && (lang as LanguageExtended).language === updatedLang.language);

          if (isDuplicate) {
            this.toastService.error(this.translocoService.translate('common.error'), this.translocoService.translate('profile.languages.duplicateError'));
            return;
          }
        }

        // Check for duplicate skill (excluding current item being edited)
        if (itemType === 'skills') {
          const updatedSkill = item as SkillExtended;
          const isDuplicate = currentItems.some((skill, i) => i !== index && (skill as SkillExtended).skillName === updatedSkill.skillName);

          if (isDuplicate) {
            this.toastService.error(this.translocoService.translate('common.error'), this.translocoService.translate('profile.skills.duplicateError'));
            return;
          }
        }

        updatedItems = [...currentItems];
        updatedItems[index] = item;
        // For edit, filter out unsaved AI-parsed items except the one being edited
        filteredItemsForBackend = currentItems.filter((currentItem, i) => {
          if (i === index) return true; // Always include the item being edited
          return !currentItem.isAiParsed || currentItem.id; // Only include non-AI-parsed or already saved items
        });
        // Find the new index of the edited item in the filtered array and update it
        let newIndex = 0;
        for (let i = 0; i < index; i++) {
          if (!currentItems[i].isAiParsed || currentItems[i].id) {
            newIndex++;
          }
        }
        filteredItemsForBackend[newIndex] = item;
        break;
      case 'delete':
        if (index === undefined) return;
        // For local UI update, simply remove the deleted item
        updatedItems = [...currentItems];
        updatedItems.splice(index, 1);
        // For backend request, filter out unsaved AI-parsed items AND the deleted item
        filteredItemsForBackend = currentItems.filter((currentItem, i) => {
          if (i === index) return false; // Exclude the item being deleted
          return !currentItem.isAiParsed || currentItem.id; // Only include non-AI-parsed or already saved items
        });
        break;
      default:
        return;
    }

    // Only create service calls if user is logged in
    let serviceCall = null;

    if (this.authService.isLoggedIn()) {
      if (itemType === 'languages') {
        serviceCall = this.userProfileService.updateLanguages(filteredItemsForBackend as LanguageExtended[]);
      } else if (itemType === 'skills') {
        serviceCall = this.userProfileService.updateSkills(filteredItemsForBackend as SkillExtended[]);
      } else if (itemType === 'highSchool') {
        serviceCall = this.userProfileService.updateHighSchoolEducation(filteredItemsForBackend as HighSchoolEducationExtended[]);
      } else if (itemType === 'university') {
        serviceCall = this.userProfileService.updateUniversityEducation(filteredItemsForBackend as UniversityEducationExtended[]);
      } else if (itemType === 'workExperience') {
        serviceCall = this.userProfileService.updateWorkExperience(filteredItemsForBackend as WorkExperienceExtended[]);
      } else if (itemType === 'projects') {
        serviceCall = this.userProfileService.updateProjects(filteredItemsForBackend as ProjectExtended[]);
      }
    }

    // Store reference to deleted item for localStorage cleanup if it was AI-parsed
    const deletedItem = action === 'delete' && index !== undefined ? currentItems[index] : null;

    // Update the local user profile data immediately for responsive UI using the full updatedItems
    if (this.fullUserProfile) {
      if (itemType === 'languages') {
        this.fullUserProfile.languages = updatedItems as LanguageExtended[];
      } else if (itemType === 'skills') {
        this.fullUserProfile.skills = updatedItems as SkillExtended[];
      } else if (itemType === 'highSchool') {
        this.fullUserProfile.highSchoolEducations = updatedItems as HighSchoolEducationExtended[];
      } else if (itemType === 'university') {
        this.fullUserProfile.universityEducations = updatedItems as UniversityEducationExtended[];
      } else if (itemType === 'workExperience') {
        this.fullUserProfile.workExperiences = updatedItems as WorkExperienceExtended[];
      } else if (itemType === 'projects') {
        this.fullUserProfile.projects = updatedItems as ProjectExtended[];
      }
    }

    // Also update userProfile for consistency (especially important for non-logged-in users)
    if (this.userProfile) {
      if (itemType === 'languages') {
        this.userProfile.languages = updatedItems as LanguageExtended[];
      } else if (itemType === 'skills') {
        this.userProfile.skills = updatedItems as SkillExtended[];
      } else if (itemType === 'highSchool') {
        this.userProfile.highSchoolEducations = updatedItems as HighSchoolEducationExtended[];
      } else if (itemType === 'university') {
        this.userProfile.universityEducations = updatedItems as UniversityEducationExtended[];
      } else if (itemType === 'workExperience') {
        this.userProfile.workExperiences = updatedItems as WorkExperienceExtended[];
      } else if (itemType === 'projects') {
        this.userProfile.projects = updatedItems as ProjectExtended[];
      }
    }

    // Emit change event for localStorage persistence (for non-logged-in users)
    this.profileDataChange.emit();

    // Only subscribe if we have a service call to make
    if (serviceCall) {
      serviceCall.subscribe({
        next: () => {
          // If we deleted an AI-parsed item, remove it from localStorage
          if (deletedItem && deletedItem.isAiParsed) {
            this.removeFromLocalStorage(deletedItem, this.getLocalStorageKey(itemType));
          }

          // Show success message based on item type
          let successMessage = '';
          if (itemType === 'languages') {
            successMessage = this.translocoService.translate('profile.messages.languagesUpdated');
          } else if (itemType === 'skills') {
            successMessage = this.translocoService.translate('profile.messages.skillsUpdated');
          } else if (itemType === 'highSchool' || itemType === 'university') {
            successMessage = this.translocoService.translate('profile.messages.educationUpdated');
          } else if (itemType === 'workExperience') {
            successMessage = this.translocoService.translate('profile.messages.workExperienceUpdated');
          } else if (itemType === 'projects') {
            successMessage = this.translocoService.translate('profile.messages.projectsUpdated');
          }

          this.toastService.success(this.translocoService.translate('profile.toast.updateSuccess'), successMessage, true, 5000);
        },
        error: (error) => {
          console.error(`Error updating ${itemType}:`, error);
          this.toastService.error(
            this.translocoService.translate('profile.toast.updateErrorTitle'),
            this.translocoService.translate('profile.messages.updateError'),
            true,
            5000
          );

          // Reload profile to get back to the original state in case of error
          this.loadUserProfile();
        },
      });
    } else {
      // User not logged in - show local save message
      this.toastService.success(
        this.translocoService.translate('profile.toast.updateSuccess'),
        this.translocoService.translate('profile.toast.changesSavedLocally'),
        true,
        3000
      );
    }
  }

  /**
   * Refresh profile data after successful save operations
   * This ensures we get the updated items with IDs and removes duplicates
   */
  private refreshProfileDataAfterSave(itemType: string, savedCount: number): void {
    // Use a small delay to ensure backend processing is complete
    setTimeout(() => {
      this.userProfileService.getUserProfile().subscribe({
        next: (updatedProfile) => {
          if (updatedProfile) {
            // Update the profile data with fresh data from backend
            this.userProfile = updatedProfile;
            this.fullUserProfile = {
              ...updatedProfile,
              firstName: this.getCurrentUser()?.firstName || '',
              lastName: this.getCurrentUser()?.lastName || '',
              email: this.getCurrentUser()?.email || '',
              language: this.getCurrentUser()?.language || '',
              aiMatching: this.getCurrentUser()?.aiMatching || false,
            };

            // Additional localStorage cleanup to ensure no duplicates remain
            this.cleanupLocalStorageAfterSave(itemType);
          }
        },
        error: (error) => {
          console.warn(`ProfileForm: Failed to refresh profile data after ${itemType} save:`, error);
          // Fallback: manually update the UI items to prevent duplicates
          this.markSavedItemsInUI(itemType);
        },
      });
    }, 500); // Small delay to ensure backend processing is complete
  }

  /**
   * Mark saved items in the UI to prevent duplicates when refetch fails
   */
  private markSavedItemsInUI(itemType: string): void {
    if (!this.fullUserProfile) return;

    // Mark all AI-parsed items without IDs as saved (give them temporary IDs)
    const sections = {
      languages: this.fullUserProfile.languages,
      skills: this.fullUserProfile.skills,
      highSchool: this.fullUserProfile.highSchoolEducations,
      university: this.fullUserProfile.universityEducations,
      workExperience: this.fullUserProfile.workExperiences,
      projects: this.fullUserProfile.projects,
    };

    const items = sections[itemType as keyof typeof sections];
    if (items) {
      items.forEach((item: any) => {
        if (item.isAiParsed && !item.id) {
          // Give it a temporary ID and mark as no longer AI-parsed
          item.id = Date.now() + Math.random();
          item.isAiParsed = false;
        }
      });
    }
  }

  /**
   * Additional cleanup of localStorage after successful save
   */
  private cleanupLocalStorageAfterSave(itemType: string): void {
    try {
      const parsedData = localStorage.getItem('parsedResumeData');
      if (parsedData) {
        const data = JSON.parse(parsedData);
        const sectionKey = this.getLocalStorageKey(itemType);

        // Get current saved items from the profile to compare
        const savedItems = this.getSavedItemsByType(itemType);

        if (data[sectionKey] && Array.isArray(data[sectionKey])) {
          const originalLength = data[sectionKey].length;

          // Remove items from localStorage that now exist in the saved profile data
          // but only if the save was actually successful
          data[sectionKey] = data[sectionKey].filter((storedItem: any) => {
            // Check if this localStorage item now exists as a saved item in the profile
            const existsInProfile = savedItems.some((profileItem: any) => {
              return this.itemsMatch(storedItem, profileItem, sectionKey);
            });

            // For work experiences, add additional validation for employer length
            if (sectionKey === 'workExperiences' && storedItem.employer && storedItem.employer.length > 20) {
              return true; // Keep items with validation issues so they can be fixed
            }

            // Keep items that don't exist in saved profile data
            return !existsInProfile;
          });

          const removedCount = originalLength - data[sectionKey].length;
          if (removedCount > 0) {
            localStorage.setItem('parsedResumeData', JSON.stringify(data));
            console.log(`ProfileForm: Removed ${removedCount} saved items from localStorage section: ${sectionKey}`);
          }
        }
      }
    } catch (error) {
      console.warn(`ProfileForm: Error during enhanced localStorage cleanup for ${itemType}:`, error);
    }
  }

  /**
   * Get saved items by type from the current profile
   */
  private getSavedItemsByType(itemType: string): any[] {
    if (!this.fullUserProfile) return [];

    switch (itemType) {
      case 'languages':
        return (this.fullUserProfile.languages || []).filter((item) => item.id && !item.isAiParsed);
      case 'skills':
        return (this.fullUserProfile.skills || []).filter((item) => item.id && !item.isAiParsed);
      case 'highSchool':
        return (this.fullUserProfile.highSchoolEducations || []).filter((item) => item.id && !item.isAiParsed);
      case 'university':
        return (this.fullUserProfile.universityEducations || []).filter((item) => item.id && !item.isAiParsed);
      case 'workExperience':
        return (this.fullUserProfile.workExperiences || []).filter((item) => item.id && !item.isAiParsed);
      case 'projects':
        return (this.fullUserProfile.projects || []).filter((item) => item.id && !item.isAiParsed);
      default:
        return [];
    }
  }

  /**
   * Check if two items match based on their key properties
   */
  private itemsMatch(localStorageItem: any, profileItem: any, sectionKey: string): boolean {
    switch (sectionKey) {
      case 'languages':
        return localStorageItem.language === profileItem.language && localStorageItem.level === profileItem.level;
      case 'skills':
        return localStorageItem.skillName === profileItem.skillName && localStorageItem.level === profileItem.level;
      case 'highSchool':
        return (
          localStorageItem.name === profileItem.highSchoolName && localStorageItem.country === profileItem.country && localStorageItem.city === profileItem.city
        );
      case 'university':
      case 'universityEducations':
        return localStorageItem.universityName === profileItem.universityName && localStorageItem.fieldOfStudy === profileItem.fieldOfStudy;
      case 'workExperiences':
        return localStorageItem.position === profileItem.position && localStorageItem.employer === profileItem.employer;
      case 'projects':
        return localStorageItem.name === profileItem.title && localStorageItem.description === profileItem.description;
      default:
        return false;
    }
  }

  /**
   * Save an individual AI-parsed item to the backend
   * @param itemType The type of item being saved
   * @param item The item to save
   * @param index The index of the item in the array
   */
  saveIndividualItem(itemType: 'languages' | 'skills' | 'highSchool' | 'university' | 'workExperience' | 'projects', item: any, index?: number): void {
    if (!this.fullUserProfile || index === undefined) return;

    const rowKey = `${itemType}_${index}`;
    const wasAiParsed = item.isAiParsed;

    // Mark as no longer AI parsed since user is manually saving it
    if (item.isAiParsed) {
      item.isAiParsed = false;
    }

    // Show loading status
    this.savingRows[rowKey] = true;
    this.rowErrors[rowKey] = null;

    let serviceCall;
    let currentItems: any[] = [];

    // Get current items and filter to only include saved items plus the one being saved
    switch (itemType) {
      case 'languages':
        currentItems = [...(this.fullUserProfile.languages || [])];
        // Filter to only send saved items (those with IDs) plus the current item being saved
        const filteredLanguages = currentItems.filter(
          (lang, i) =>
            i === index || // Always include the current item being saved
            !lang.isAiParsed || // Include non-AI-parsed items
            lang.id // Include AI-parsed items that already have IDs (already saved)
        );
        serviceCall = this.userProfileService.updateLanguages(filteredLanguages as LanguageExtended[]);
        break;
      case 'skills':
        currentItems = [...(this.fullUserProfile.skills || [])];
        // Filter to only send saved items (those with IDs) plus the current item being saved
        const filteredSkills = currentItems.filter(
          (skill, i) =>
            i === index || // Always include the current item being saved
            !skill.isAiParsed || // Include non-AI-parsed items
            skill.id // Include AI-parsed items that already have IDs (already saved)
        );
        serviceCall = this.userProfileService.updateSkills(filteredSkills as SkillExtended[]);
        break;
      case 'highSchool':
        currentItems = [...(this.fullUserProfile.highSchoolEducations || [])];
        const filteredHighSchools = currentItems.filter((hs, i) => i === index || !hs.isAiParsed || hs.id);
        serviceCall = this.userProfileService.updateHighSchoolEducation(filteredHighSchools as HighSchoolEducationExtended[]);
        break;
      case 'university':
        currentItems = [...(this.fullUserProfile.universityEducations || [])];
        const filteredUniversities = currentItems.filter((uni, i) => i === index || !uni.isAiParsed || uni.id);
        serviceCall = this.userProfileService.updateUniversityEducation(filteredUniversities as UniversityEducationExtended[]);
        break;
      case 'workExperience':
        currentItems = [...(this.fullUserProfile.workExperiences || [])];
        const filteredWorkExperiences = currentItems.filter((work, i) => i === index || !work.isAiParsed || work.id);
        serviceCall = this.userProfileService.updateWorkExperience(filteredWorkExperiences as WorkExperienceExtended[]);
        break;
      case 'projects':
        currentItems = [...(this.fullUserProfile.projects || [])];
        const filteredProjects = currentItems.filter((project, i) => i === index || !project.isAiParsed || project.id);
        serviceCall = this.userProfileService.updateProjects(filteredProjects as ProjectExtended[]);
        break;
      default:
        console.error('Unknown item type:', itemType);
        this.savingRows[rowKey] = false;
        this.rowErrors[rowKey] = 'Unknown item type';
        return;
    }

    serviceCall
      .pipe(
        finalize(() => {
          this.savingRows[rowKey] = false;
        })
      )
      .subscribe({
        next: (response) => {
          // Clear error state
          this.rowErrors[rowKey] = null;

          // Update the item with ID if it was provided in response
          if (response?.body?.id && !item.id) {
            item.id = response.body.id;
          } else if (!item.id) {
            // Generate a temporary ID to show the saved indicator
            item.id = Date.now() + index;
          }

          // If this was an AI-parsed entry that was successfully saved, remove it from localStorage
          if (wasAiParsed && item.id) {
            this.removeFromLocalStorage(item, this.getLocalStorageKey(itemType));
          }

          // Show success message
          this.toastService.success(
            this.translocoService.translate('profile.aiParsing.individualSave.successTitle'),
            this.translocoService.translate('profile.aiParsing.individualSave.itemSaved')
          );

          // Refetch profile data to ensure UI is in sync with backend
          this.refreshProfileDataAfterSave(itemType, 1);
        },
        error: (error) => {
          console.error(`Error saving individual ${itemType} item:`, error);

          // Restore AI-parsed state on error if it was originally AI-parsed
          if (wasAiParsed) {
            item.isAiParsed = true;
          }

          this.rowErrors[rowKey] = error.message || this.translocoService.translate('profile.aiParsing.individualSave.saveError');
          this.toastService.error(
            this.translocoService.translate('profile.aiParsing.individualSave.errorTitle'),
            this.translocoService.translate('profile.aiParsing.individualSave.saveError')
          );
        },
      });
  }

  /**
   * Save multiple AI-parsed items in batch to reduce API calls
   */
  saveBatchItems(itemType: 'languages' | 'skills' | 'highSchool' | 'university' | 'workExperience' | 'projects', items: any[]): void {
    if (!this.fullUserProfile || !items || items.length === 0) return;

    // Mark all items as no longer AI-parsed since they're being saved
    items.forEach((item) => {
      if (item.isAiParsed) {
        item.isAiParsed = false;
      }
    });

    let serviceCall;
    let currentItems: any[] = [];

    // Get current items and prepare the data for batch save
    // Note: Since we set isAiParsed = false above, items are already marked as saved.
    // We just need to send all current items (they're the same object references).
    switch (itemType) {
      case 'languages':
        currentItems = [...(this.fullUserProfile.languages || [])];
        serviceCall = this.userProfileService.updateLanguages(currentItems as LanguageExtended[]);
        break;
      case 'skills':
        currentItems = [...(this.fullUserProfile.skills || [])];
        serviceCall = this.userProfileService.updateSkills(currentItems as SkillExtended[]);
        break;
      case 'highSchool':
        currentItems = [...(this.fullUserProfile.highSchoolEducations || [])];
        serviceCall = this.userProfileService.updateHighSchoolEducation(currentItems as HighSchoolEducationExtended[]);
        break;
      case 'university':
        currentItems = [...(this.fullUserProfile.universityEducations || [])];
        serviceCall = this.userProfileService.updateUniversityEducation(currentItems as UniversityEducationExtended[]);
        break;
      case 'workExperience':
        currentItems = [...(this.fullUserProfile.workExperiences || [])];
        serviceCall = this.userProfileService.updateWorkExperience(currentItems as WorkExperienceExtended[]);
        break;
      case 'projects':
        currentItems = [...(this.fullUserProfile.projects || [])];
        serviceCall = this.userProfileService.updateProjects(currentItems as ProjectExtended[]);
        break;
      default:
        console.error('Unknown item type for batch save:', itemType);
        return;
    }

    serviceCall.subscribe({
      next: (response) => {
        // Update items with IDs if provided in response
        if (response?.body && Array.isArray(response.body)) {
          items.forEach((item, index) => {
            if (response.body[index]?.id && !item.id) {
              item.id = response.body[index].id;
            } else if (!item.id) {
              // Generate a temporary ID to show the saved indicator
              item.id = Date.now() + index;
            }
          });
        } else {
          // Fallback: generate temporary IDs for all items
          items.forEach((item, index) => {
            if (!item.id) {
              item.id = Date.now() + index;
            }
          });
        }

        // Remove batch saved items from localStorage
        const localStorageKey = this.getLocalStorageKey(itemType);
        items.forEach((item) => {
          this.removeFromLocalStorage(item, localStorageKey);
        });

        // Refetch profile data to ensure UI is in sync with backend
        this.refreshProfileDataAfterSave(itemType, items.length);
      },
      error: (error) => {
        console.error(`Error batch saving ${itemType} items:`, error);

        // Keep items in the UI when save fails by restoring AI-parsed state
        items.forEach((item) => {
          item.isAiParsed = true;
        });

        // Display error notification
        this.toastService.error(
          this.translocoService.translate('profile.aiParsing.batchSave.errorTitle'),
          this.translocoService.translate('profile.aiParsing.batchSave.saveError')
        );
      },
    });
  }

  /**
   * Remove an autofilled item from localStorage to prevent it from being re-added
   * @param item The item to remove
   * @param sectionKey The section key in localStorage
   */
  private removeFromLocalStorage(item: any, sectionKey: string): void {
    const parsedData = localStorage.getItem('parsedResumeData');
    if (parsedData) {
      const data = JSON.parse(parsedData);

      if (data[sectionKey] && Array.isArray(data[sectionKey])) {
        const originalLength = data[sectionKey].length;

        // Remove the item from localStorage data based on type
        data[sectionKey] = data[sectionKey].filter((storedItem: any) => {
          let shouldKeep = true;
          switch (sectionKey) {
            case 'languages':
              shouldKeep = !(storedItem.language === item.language && storedItem.level === item.level);
              break;
            case 'skills':
              shouldKeep = !(storedItem.skillName === item.skillName && storedItem.level === item.level);
              break;
            case 'highSchool':
              shouldKeep = !(storedItem.name === item.highSchoolName && storedItem.country === item.country && storedItem.city === item.city);
              break;
            case 'university':
            case 'universityEducations':
              shouldKeep = !(storedItem.universityName === item.universityName && storedItem.fieldOfStudy === item.fieldOfStudy);
              break;
            case 'workExperiences':
              shouldKeep = !(storedItem.position === item.position && storedItem.employer === item.employer);
              break;
            case 'projects':
              shouldKeep = !(storedItem.name === item.name && storedItem.description === item.description);
              break;
            default:
              shouldKeep = true;
          }
          return shouldKeep;
        });

        // Update localStorage
        localStorage.setItem('parsedResumeData', JSON.stringify(data));
      }
    }
  }

  /**
   * Get the localStorage key for a given item type
   * @param itemType The item type
   * @returns The corresponding localStorage key
   */
  private getLocalStorageKey(itemType: string): string {
    switch (itemType) {
      case 'languages':
        return 'languages';
      case 'skills':
        return 'skills';
      case 'highSchool':
        return 'highSchool';
      case 'university':
        return 'university';
      case 'workExperience':
        return 'workExperiences';
      case 'projects':
        return 'projects';
      default:
        return itemType;
    }
  }

  /**
   * Check if a row is currently being saved
   * @param itemType The item type
   * @param index The index of the row
   */
  isRowSaving(itemType: string, index: number): boolean {
    const rowKey = `${itemType}_${index}`;
    return this.savingRows[rowKey] === true;
  }

  /**
   * Get error message for a row if there was an error saving
   * @param itemType The item type
   * @param index The index of the row
   */
  getRowError(itemType: string, index: number): string | null {
    const rowKey = `${itemType}_${index}`;
    return this.rowErrors[rowKey] || null;
  }

  /**
   * Legacy handlers for backward compatibility
   * These methods simply pass the data to the unified handler
   */
  onAddLanguage(language: LanguageExtended): void {
    const event = { itemType: 'languages' as const, action: 'add' as const, item: language };
    this.onProfileItemAction(event);
  }

  onEditLanguage(data: { language: LanguageExtended; index: number }): void {
    const event = {
      itemType: 'languages' as const,
      action: 'edit' as const,
      item: data.language,
      index: data.index,
    };
    this.onProfileItemAction(event);
  }

  onDeleteLanguage(index: number): void {
    const event = { itemType: 'languages' as const, action: 'delete' as const, index };
    this.onProfileItemAction(event);
  }

  onAddSkill(skill: SkillExtended): void {
    const event = { itemType: 'skills' as const, action: 'add' as const, item: skill };
    this.onProfileItemAction(event);
  }

  onEditSkill(data: { skill: SkillExtended; index: number }): void {
    const event = {
      itemType: 'skills' as const,
      action: 'edit' as const,
      item: data.skill,
      index: data.index,
    };
    this.onProfileItemAction(event);
  }

  onDeleteSkill(index: number): void {
    const event = { itemType: 'skills' as const, action: 'delete' as const, index };
    this.onProfileItemAction(event);
  }

  /**
   * Legacy handlers for high school education
   * These methods simply pass the data to the unified handler
   */
  onAddHighSchool(highSchool: HighSchoolEducationExtended): void {
    this.onProfileItemAction({ itemType: 'highSchool', action: 'add', item: highSchool });
  }

  onEditHighSchool(data: { highSchool: HighSchoolEducationExtended; index: number }): void {
    this.onProfileItemAction({ itemType: 'highSchool', action: 'edit', item: data.highSchool, index: data.index });
  }

  onDeleteHighSchool(index: number): void {
    this.onProfileItemAction({ itemType: 'highSchool', action: 'delete', index });
  }

  // Handlers for university education
  // These methods simply pass the data to the unified handler
  onAddUniversity(university: UniversityEducationExtended): void {
    this.onProfileItemAction({ itemType: 'university', action: 'add', item: university });
  }

  onEditUniversity(data: { university: UniversityEducationExtended; index: number }): void {
    this.onProfileItemAction({ itemType: 'university', action: 'edit', item: data.university, index: data.index });
  }

  onDeleteUniversity(index: number): void {
    this.onProfileItemAction({ itemType: 'university', action: 'delete', index });
  }

  // Work Experience handlers
  onAddWorkExperience(workExperience: WorkExperienceExtended): void {
    this.onProfileItemAction({ itemType: 'workExperience', action: 'add', item: workExperience });
  }

  onEditWorkExperience(data: { workExperience: WorkExperienceExtended; index: number }): void {
    this.onProfileItemAction({ itemType: 'workExperience', action: 'edit', item: data.workExperience, index: data.index });
  }

  onDeleteWorkExperience(index: number): void {
    this.onProfileItemAction({ itemType: 'workExperience', action: 'delete', index });
  }

  /**
   * Check for missing required profile fields
   */
  checkMissingProfileFields(): void {
    if (!this.fullUserProfile) return;

    const requiredFields: { field: string; label: string; isAddressField?: boolean }[] = [
      { field: 'firstName', label: 'profile.personal.firstName' },
      { field: 'lastName', label: 'profile.personal.lastName' },
      { field: 'gender', label: 'profile.personal.gender' },
      { field: 'telephone', label: 'profile.personal.phone' },
      { field: 'birthDate', label: 'profile.personal.dateOfBirth' },
      { field: 'streetName', label: 'profile.personal.streetName', isAddressField: true },
      { field: 'streetNumber', label: 'profile.personal.streetNumber', isAddressField: true },
      { field: 'city', label: 'profile.personal.city', isAddressField: true },
      { field: 'postalCode', label: 'profile.personal.zipCode', isAddressField: true },
      { field: 'country', label: 'profile.personal.country', isAddressField: true },
    ];

    // Store missing fields with their translation keys
    const missingFields = requiredFields.filter((item) => {
      if (item.isAddressField) {
        const value = this.fullUserProfile?.address?.[item.field as keyof typeof this.fullUserProfile.address];
        return !value || value === '';
      } else {
        const value = this.fullUserProfile?.[item.field as keyof FullUserProfile];
        return !value || value === '';
      }
    });

    // Update the missingProfileFields array with just the translation keys for display
    this.missingProfileFields = missingFields.map((item) => item.label);
  }

  /**
   * Validate form fields and update missingProfileFields for the alert display
   * This checks the current form values (not fullUserProfile) during form submission
   */
  validateFormAndUpdateMissingFields(): void {
    const requiredFields: { formField: string; label: string }[] = [
      { formField: 'firstName', label: 'profile.personal.firstName' },
      { formField: 'lastName', label: 'profile.personal.lastName' },
      { formField: 'gender', label: 'profile.personal.gender' },
      { formField: 'phone', label: 'profile.personal.phone' },
      { formField: 'dateOfBirth', label: 'profile.personal.dateOfBirth' },
      { formField: 'streetName', label: 'profile.personal.streetName' },
      { formField: 'streetNumber', label: 'profile.personal.streetNumber' },
      { formField: 'city', label: 'profile.personal.city' },
      { formField: 'zipCode', label: 'profile.personal.zipCode' },
      { formField: 'country', label: 'profile.personal.country' },
    ];

    const missingFields = requiredFields.filter((item) => {
      const value = this.personalInfoForm.get(item.formField)?.value;
      return !value || (typeof value === 'string' && !value.trim());
    });

    // Update the missingProfileFields array with translation keys for display
    this.missingProfileFields = missingFields.map((item) => item.label);
  }

  /**
   * Validate that all required basic profile fields are filled
   * Returns array of missing field names (translated) or empty array if all fields are valid
   */
  private validateBasicProfileFields(): string[] {
    const missingFields: string[] = [];

    // Validate firstName
    if (!this.personalInfoForm.get('firstName')?.value?.trim()) {
      missingFields.push(this.translocoService.translate('profile.personal.firstName'));
    }

    // Validate lastName
    if (!this.personalInfoForm.get('lastName')?.value?.trim()) {
      missingFields.push(this.translocoService.translate('profile.personal.lastName'));
    }

    // Validate gender
    if (!this.personalInfoForm.get('gender')?.value?.trim()) {
      missingFields.push(this.translocoService.translate('profile.personal.gender'));
    }

    // Validate phone
    if (!this.personalInfoForm.get('phone')?.value?.trim()) {
      missingFields.push(this.translocoService.translate('profile.personal.phone'));
    }

    // Validate dateOfBirth
    if (!this.personalInfoForm.get('dateOfBirth')?.value?.trim()) {
      missingFields.push(this.translocoService.translate('profile.personal.dateOfBirth'));
    }

    // Validate streetName
    if (!this.personalInfoForm.get('streetName')?.value?.trim()) {
      missingFields.push(this.translocoService.translate('profile.personal.streetName'));
    }

    // Validate streetNumber
    if (!this.personalInfoForm.get('streetNumber')?.value) {
      missingFields.push(this.translocoService.translate('profile.personal.streetNumber'));
    }

    // Validate city
    if (!this.personalInfoForm.get('city')?.value?.trim()) {
      missingFields.push(this.translocoService.translate('profile.personal.city'));
    }

    // Validate zipCode (postalCode)
    if (!this.personalInfoForm.get('zipCode')?.value) {
      missingFields.push(this.translocoService.translate('profile.personal.zipCode'));
    }

    // Validate country
    if (!this.personalInfoForm.get('country')?.value?.trim()) {
      missingFields.push(this.translocoService.translate('profile.personal.country'));
    }

    return missingFields;
  }

  /**
   * Validate basic profile fields and show error toast if validation fails
   * Returns true if validation passed, false otherwise
   */
  private validateAndShowErrorIfNeeded(): boolean {
    const missingFields = this.validateBasicProfileFields();

    if (missingFields.length > 0) {
      const errorMessage = this.translocoService.translate('profile.aiParsing.validation.requiredFieldsMessage', { fields: missingFields.join(', ') });
      this.toastService.error(this.translocoService.translate('profile.aiParsing.validation.requiredFieldsTitle'), errorMessage);
      return false;
    }

    return true;
  }

  // ===============================
  // Profile Completion Methods
  // ===============================

  /**
   * Handle selection of AI parsing method
   */
  onChooseAiParsing(): void {
    this.profileCompletionMethod = 'ai';
    this.showProfileSectionsForMethod = false; // Don't show sections until parsing is complete
    this.showAiParsing();
  }

  /**
   * Handle selection of manual entry method (called from profile-personal-info-preview)
   */
  onChooseManualEntry(): void {
    // Validate before allowing manual entry
    if (!this.validateAndShowErrorIfNeeded()) {
      return;
    }

    // All validation passed, proceed with manual entry
    this.profileCompletionMethod = 'manual';
    this.showProfileSectionsForMethod = true; // Show sections immediately for manual entry
    this.hideAiParsing(); // Hide AI parsing section
    this.isParsingComplete = false; // Reset parsing state
    this.parsingSuccess = false;
    this.resetFileUpload(); // Reset any uploaded files
  }

  /**
   * Go back to choose completion method
   */
  goBackToMethodSelection(): void {
    this.profileCompletionMethod = null;
    this.showProfileSectionsForMethod = false;
    this.hideAiParsing();
    // Reset AI parsing state
    this.isParsingComplete = false;
    this.parsingSuccess = false;
    this.resetFileUpload();
  }

  // ===============================
  // AI Parsing Methods
  // ===============================

  /**
   * Show AI parsing section
   */
  showAiParsing(): void {
    this.showAiParsingSection = true;
    this.resetFileUpload();
  }

  /**
   * Hide AI parsing section
   * @param preserveUploadedFile - Whether to keep the uploaded file information
   */
  hideAiParsing(preserveUploadedFile = false): void {
    this.showAiParsingSection = false;
    if (!preserveUploadedFile) {
      this.resetFileUpload();
    }
  }

  /**
   * Handle file selection from input
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFile(input.files[0]);
    }
  }

  /**
   * Handle drag over event
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  /**
   * Handle drag leave event
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  /**
   * Handle file drop event
   */
  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  /**
   * Process uploaded file
   */
  processFile(file: File): void {
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      this.toastService.error(
        this.translocoService.translate('profile.aiParsing.validation.invalidFormat'),
        this.translocoService.translate('profile.aiParsing.dropzone.supportedFormats')
      );
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      this.toastService.error(
        this.translocoService.translate('profile.aiParsing.validation.fileTooLarge'),
        this.translocoService.translate('profile.aiParsing.validation.fileTooLarge')
      );
      return;
    }

    this.uploadedFile = file;
    this.isFileUploaded = true;

    // Create preview for image files (if needed)
    if (file.type.includes('image')) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.filePreviewUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      this.filePreviewUrl = null;
    }
  }

  /**
   * Remove uploaded file
   */
  removeFile(): void {
    this.resetFileUpload();
  }

  /**
   * Reset file upload state
   * @param preserveFileInfo - Whether to keep the file information for display purposes
   */
  private resetFileUpload(preserveFileInfo = false): void {
    if (!preserveFileInfo) {
      this.uploadedFile = null;
      this.isFileUploaded = false;
      this.filePreviewUrl = null;
      // Reset form file field only if not preserving
      this.resumeAndCoverForm.patchValue({
        resumeFile: null,
      });
      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
      }
    }
    this.parsingError = null;
    this.rateLimitInfo = null;
    this.isParsingComplete = false;
    this.parsingSuccess = false;
    this.currentStep = 1;
  }

  /**
   * Start AI parsing of the uploaded resume
   */
  parseWithAI(): void {
    const now = Date.now();

    // Guard against multiple calls while parsing is in progress
    if (this.isParsingInProgress || this.parseWithAIInProgress) {
      console.log('ProfileForm: Parsing already in progress, ignoring duplicate call');
      return;
    }

    // Guard against rapid successive calls (within 2 seconds)
    if (now - this.lastParseAttemptTime < 2000) {
      console.log('ProfileForm: Ignoring rapid successive parseWithAI call, last attempt was', now - this.lastParseAttemptTime, 'ms ago');
      return;
    }

    // Set flags to prevent concurrent calls and show loader
    this.parseWithAIInProgress = true;
    // Don't show loader yet - wait until validation passes
    this.lastParseAttemptTime = now;

    if (!this.uploadedFile) {
      console.log('ProfileForm: No uploaded file, showing error');
      this.toastService.error(
        this.translocoService.translate('profile.aiParsing.validation.fileRequired'),
        this.translocoService.translate('profile.aiParsing.validation.fileRequired')
      );
      this.parseWithAIInProgress = false;
      return;
    }

    // Validate that all required basic profile fields are filled
    if (!this.validateAndShowErrorIfNeeded()) {
      this.parseWithAIInProgress = false;
      return;
    }

    // All validation passed, build personal info for CV parsing
    // For logged-in users, get email from auth service; for non-logged in, use userEmail from parent
    const email = this.authService.isLoggedIn() ? this.authService.getCurrentUser()?.email || this.userEmail || '' : this.userEmail || '';
    const personalInfo: ParseCvPersonalInfo = {
      firstName: this.personalInfoForm.get('firstName')?.value || '',
      lastName: this.personalInfoForm.get('lastName')?.value || '',
      email: email,
      phone: this.normalizePhoneValue(this.personalInfoForm.get('phone')?.value, this.personalInfoForm.get('phoneCountry')?.value),
      dob: this.personalInfoForm.get('dateOfBirth')?.value || '',
      address: this.personalInfoForm.get('streetName')?.value || '',
      city: this.personalInfoForm.get('city')?.value || '',
      houseNumber: String(this.personalInfoForm.get('streetNumber')?.value || ''),
    };

    this.cvParsingService
      .parseCvFile(this.uploadedFile, personalInfo, this.aiParsingConsent, this.resumeLanguage, true)
      .pipe(
        tap(() => {
          this.startParsingSteps();
        }),
        finalize(() => {
          this.parseWithAIInProgress = false;
        })
      )
      .subscribe({
        next: (response) => {
          // The service handles the success notification and data storage
        },
        error: (error) => {
          // Check for rate limit error (429) - check status directly as instanceof can fail across module contexts
          const isRateLimitError = error?.status === 429;

          if (isRateLimitError) {
            // Extract rate limit info from response body
            const body = error.error || {};
            this.rateLimitInfo = {
              message: body.message,
              retryAfterHours: body.retryAfterHours,
              retryAfterMinutes: body.retryAfterMinutes,
              retryAfter: body.retryAfter,
            };
          } else {
            // Show user-friendly error message for other errors
            let errorMessage = this.translocoService.translate('profile.toast.cvParsingError');
            if (typeof error === 'string') {
              errorMessage = error;
            } else if (error?.message) {
              errorMessage = error.message;
            }

            // Show toast notification to user
            this.toastService.error(this.translocoService.translate('profile.aiParsing.notifications.parseError'), errorMessage);
            this.parsingError = errorMessage;
          }

          // Reset parsing state
          this.isParsingComplete = false;
          this.parsingSuccess = false;
          this.isParsingInProgress = false;
        },
      });
  }

  /**
   * Start parsing steps animation
   */
  private startParsingSteps(): void {
    this.currentStep = 1;
    this.isParsingInProgress = true;

    // Simulate step progression for better UX
    const stepInterval = setInterval(() => {
      if (this.currentStep < this.maxSteps - 1 && this.isParsingInProgress) {
        this.currentStep++;
      } else {
        clearInterval(stepInterval);
      }
    }, 1500); // Move to next step every 1.5 seconds
  }

  /**
   * Format file size for display
   */
  formatFileSize(size: number): string {
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file type label for display
   */
  getFileTypeLabel(): string {
    if (!this.uploadedFile) return '';

    const extension = this.uploadedFile.name.split('.').pop()?.toUpperCase();
    switch (extension) {
      case 'PDF':
        return 'PDF Document';
      case 'DOC':
      case 'DOCX':
        return 'Word Document';
      default:
        return 'Document';
    }
  }

  /**
   * Retry parsing after error
   */
  retryParsing(): void {
    this.parsingError = null;
    this.rateLimitInfo = null;
    this.parseWithAI();
  }

  /**
   * Start over with a new file
   */
  startOver(): void {
    this.resetFileUpload();
    this.showAiParsingSection = true;
  }

  /**
   * Load autofilled data from localStorage if available
   * Similar to the candidate app's loadAutofillData methods
   */
  loadAutofillData(): void {
    this.hasPerformedAutosave = false;

    const parsedData = localStorage.getItem('parsedResumeData');
    const autofillSections = localStorage.getItem('autofillSections');

    if (parsedData) {
      const data = JSON.parse(parsedData);
      let sections: any = {};
      let hasDataLoaded = false;

      if (autofillSections) {
        sections = JSON.parse(autofillSections);
      } else {
        sections = {
          languages: true,
          skills: true,
          workExperiences: true,
          projects: true,
          university: true,
          highSchool: true,
          driversLicenses: true,
        };
      }

      // FORCE LOAD SKILLS - Load skills regardless of sections config if data exists
      if (data.skills && data.skills.length > 0) {
        this.loadSkillsFromParsedData(data.skills);
        hasDataLoaded = true;
      }

      // FORCE LOAD WORK EXPERIENCES - Load work experiences regardless of sections config if data exists
      if (data.workExperiences && data.workExperiences.length > 0) {
        // Remove duplicates before loading
        const uniqueWorkExperiences = this.removeDuplicatesFromParsedData(data.workExperiences, 'workExperience');
        this.loadWorkExperiencesFromParsedData(uniqueWorkExperiences);
        hasDataLoaded = true;
      }

      // Load languages
      if (sections.languages && data.languages && data.languages.length > 0) {
        this.loadLanguagesFromParsedData(data.languages);
        hasDataLoaded = true;
      }

      // Load projects
      if (sections.projects && data.projects && data.projects.length > 0) {
        this.loadProjectsFromParsedData(data.projects);
        hasDataLoaded = true;
      }

      // FORCE LOAD UNIVERSITY EDUCATION - Load regardless of sections config if data exists
      if (data.university && data.university.length > 0) {
        this.loadUniversityEducationFromParsedData(data.university);
        hasDataLoaded = true;
      }

      // FORCE LOAD HIGH SCHOOL EDUCATION - Load regardless of sections config if data exists
      if (data.highSchool && data.highSchool.length > 0) {
        this.loadHighSchoolEducationFromParsedData(data.highSchool);
        hasDataLoaded = true;
      }

      // Map highestEducationLevel string to educationLevelID on the personal info form
      if (data.highestEducationLevel) {
        const educationLevelID = this.resolveEducationLevelID(data.highestEducationLevel);
        if (educationLevelID !== null) {
          this.personalInfoForm.patchValue({ educationLevelID });
        }
      }

      // Load drivers licenses
      if (sections.driversLicenses && data.driversLicenses && data.driversLicenses.length > 0) {
        this.loadDriversLicensesFromParsedData(data.driversLicenses);
        hasDataLoaded = true;
      }

      // Show success message if any data was loaded
      if (hasDataLoaded) {
        this.toastService.success(
          this.translocoService.translate('profile.aiParsing.notifications.dataLoaded'),
          this.translocoService.translate('profile.aiParsing.notifications.dataLoadedDesc')
        );

        // Set the profile completion method to AI and show profile sections
        if (!this.profileCompletionMethod) {
          this.profileCompletionMethod = 'ai';
        }
        this.showProfileSectionsForMethod = true;
        this.isParsingComplete = true;
        this.parsingSuccess = true;
      }

      // Automatically save valid parsed data to backend only if this is a recent parsing session
      if (hasDataLoaded && !this.hasAlreadyAutosaved() && this.isRecentParsingSession()) {
        this.autosaveValidParsedData(data, sections);
      } else {
        console.log('ProfileForm: Autosave conditions not met:', {
          hasDataLoaded,
          hasAlreadyAutosaved: this.hasAlreadyAutosaved(),
          isRecentParsingSession: this.isRecentParsingSession(),
        });
      }
    } else {
      console.log('ProfileForm: No parsed data found in localStorage');
    }
  }

  /**
   * Automatically save valid parsed CV data to the backend
   * This method validates and filters the data, then saves only valid entries
   */
  private autosaveValidParsedData(data: any, sections: any): void {
    // Prevent multiple simultaneous autosave operations
    if (this.autosaveInProgress) {
      return;
    }

    // Mark that we're performing autosave to prevent duplicates
    this.hasPerformedAutosave = true;
    this.autosaveInProgress = true;

    // Small delay to ensure UI is updated before saving
    setTimeout(() => {
      this.performAutosave(data, sections);
    }, 1000);
  }

  /**
   * Perform the actual autosave operation for all valid data sections
   */
  private performAutosave(data: any, sections: any): void {
    // Only perform autosave for authenticated users
    if (!this.isUserAuthenticated()) {
      this.autosaveInProgress = false;
      return;
    }

    const savePromises: Promise<any>[] = [];

    // Autosave languages if valid data exists
    if (sections.languages && data.languages && data.languages.length > 0) {
      // CV parsing service already cleaned and validated the data
      savePromises.push(
        this.performBatchSave('languages', data.languages).catch((error) => {
          return { error: error.message, type: 'languages' };
        })
      );
    } else {
      if (sections.languages) {
        console.log('ProfileForm: No valid languages to autosave');
      }
    }

    // Autosave skills if valid data exists
    if (data.skills && data.skills.length > 0) {
      // CV parsing service already cleaned and validated the data
      savePromises.push(
        this.performBatchSave('skills', data.skills).catch((error) => {
          return { error: error.message, type: 'skills' };
        })
      );
    } else {
      if (sections.skills) {
        console.log('ProfileForm: No valid skills to autosave');
      }
    }

    // Autosave work experiences if valid data exists
    if (data.workExperiences && data.workExperiences.length > 0) {
      // CV parsing service already cleaned and validated the data
      savePromises.push(
        this.performBatchSave('workExperience', data.workExperiences).catch((error) => {
          return { error: error.message, type: 'workExperience' };
        })
      );
    } else {
      if (sections.workExperiences) {
        console.log('ProfileForm: No valid work experiences to autosave');
      }
    }

    // Autosave projects if valid data exists
    if (sections.projects && data.projects && data.projects.length > 0) {
      // CV parsing service already cleaned and validated the data
      savePromises.push(
        this.performBatchSave('projects', data.projects).catch((error) => {
          return { error: error.message, type: 'projects' };
        })
      );
    } else {
      if (sections.projects) {
        console.log('ProfileForm: No valid projects to autosave');
      }
    }

    // Autosave university education if valid data exists
    if (sections.university && data.university && data.university.length > 0) {
      // CV parsing service already cleaned and validated the data
      savePromises.push(
        this.performBatchSave('university', data.university).catch((error) => {
          return { error: error.message, type: 'university' };
        })
      );
    } else {
      if (sections.university) {
        console.log('ProfileForm: No valid university educations to autosave');
      }
    }

    // Autosave high school education if valid data exists
    if (sections.highSchool && data.highSchool && data.highSchool.length > 0) {
      // CV parsing service already cleaned and validated the data
      savePromises.push(
        this.performBatchSave('highSchool', data.highSchool).catch((error) => {
          return { error: error.message, type: 'highSchool' };
        })
      );
    } else {
      if (sections.highSchool) {
        console.log('ProfileForm: No valid high school educations to autosave');
      }
    }

    // Autosave drivers licenses if valid data exists
    if (sections.driversLicenses && data.driversLicenses && data.driversLicenses.length > 0) {
      // CV parsing service already cleaned and validated the data
      savePromises.push(
        this.autosaveDriversLicenses(data.driversLicenses).catch((error) => {
          return { error: error.message, type: 'driversLicenses' };
        })
      );
    } else {
      if (sections.driversLicenses) {
        console.log('ProfileForm: No valid drivers licenses to autosave');
      }
    }

    // Execute all save operations and show result
    if (savePromises.length > 0) {
      Promise.allSettled(savePromises).then((results) => {
        const successful = results.filter((result) => result.status === 'fulfilled' && result.value && !result.value.error).length;
        const failed = results.filter((result) => result.status === 'rejected' || (result.status === 'fulfilled' && result.value && result.value.error)).length;

        if (successful > 0) {
          this.toastService.success(
            this.translocoService.translate('profile.aiParsing.autosave.successTitle'),
            this.translocoService.translate('profile.aiParsing.autosave.successMessage', { count: successful })
          );
        }

        if (failed > 0) {
          console.error(`ProfileForm: Autosave had ${failed} failures`);
          // Log the failed results for debugging
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              console.error(`ProfileForm: Autosave ${index} rejected:`, result.reason);
            } else if (result.status === 'fulfilled' && result.value && result.value.error) {
              console.error(`ProfileForm: Autosave ${index} failed:`, result.value);
            }
          });

          this.toastService.error(
            this.translocoService.translate('profile.aiParsing.autosave.partialErrorTitle'),
            this.translocoService.translate('profile.aiParsing.autosave.partialErrorMessage', { failed, successful })
          );
        }
      });
    } else {
      console.log('ProfileForm: No valid data to autosave');
    }

    // Reset autosave in progress flag
    this.autosaveInProgress = false;
  }

  /**
   * Perform batch save operation and return a promise
   */
  private performBatchSave(itemType: string, items: any[]): Promise<any> {
    return new Promise((resolve, reject) => {
      const uiItems = this.getUiItemsByType(itemType, items);

      if (uiItems.length === 0) {
        resolve(null);
        return;
      }

      let serviceCall: any;

      // Prepare service call based on item type - send only the new parsed items (replaces all existing data)
      try {
        switch (itemType) {
          case 'languages':
            serviceCall = this.userProfileService.updateLanguages(uiItems as LanguageExtended[]);
            break;
          case 'skills':
            serviceCall = this.userProfileService.updateSkills(uiItems as SkillExtended[]);
            break;
          case 'highSchool':
            serviceCall = this.userProfileService.updateHighSchoolEducation(uiItems as HighSchoolEducationExtended[]);
            break;
          case 'university':
            serviceCall = this.userProfileService.updateUniversityEducation(uiItems as UniversityEducationExtended[]);
            break;
          case 'workExperience':
            serviceCall = this.userProfileService.updateWorkExperience(uiItems as WorkExperienceExtended[]);
            break;
          case 'projects':
            serviceCall = this.userProfileService.updateProjects(uiItems as ProjectExtended[]);
            break;
          default:
            const errorMsg = `Unknown item type for autosave: ${itemType}`;
            console.error(`ProfileForm: ${errorMsg}`);
            reject(new Error(errorMsg));
            return;
        }
      } catch (error) {
        console.error(`ProfileForm: Error preparing service call for ${itemType}:`, error);
        reject(error);
        return;
      }

      if (!serviceCall) {
        console.error(`ProfileForm: No service call created for ${itemType}`);
        reject(new Error(`No service call available for ${itemType}`));
        return;
      }

      serviceCall.subscribe({
        next: (response: any) => {
          if (response?.body && Array.isArray(response.body)) {
            uiItems.forEach((item: any, index: number) => {
              if (response.body[index]?.id && !item.id) {
                item.id = response.body[index].id;
              } else if (!item.id) {
                // Generate a temporary ID to show the saved indicator
                item.id = Date.now() + index;
              }
            });
          } else {
            // Fallback: generate temporary IDs for all items
            uiItems.forEach((item: any, index: number) => {
              if (!item.id) {
                item.id = Date.now() + index;
              }
            });
          }

          // Remove autosaved items from localStorage
          try {
            // Only remove items from localStorage if save was successful
            const localStorageKey = this.getLocalStorageKey(itemType);
            uiItems.forEach((item: any) => {
              this.removeFromLocalStorage(item, localStorageKey);
            });
          } catch (error) {
            console.warn(`ProfileForm: Error removing items from localStorage for ${itemType}:`, error);
            // Don't fail the whole operation for localStorage issues
          }

          this.refreshProfileDataAfterSave(itemType, uiItems.length);

          resolve(response);
        },
        error: (error: any) => {
          console.error(`ProfileForm: Error autosaving ${itemType} items:`, error);
          console.error(`ProfileForm: Failed items:`, uiItems);

          uiItems.forEach((item: any) => {
            if (item.isAiParsed === false) {
              item.isAiParsed = true;
            }
          });

          reject(new Error(`Failed to save ${itemType}: ${error.message || error}`));
        },
      });
    });
  }

  /**
   * Get UI items by type for autosave - finds the loaded items in the profile
   */
  private getUiItemsByType(itemType: string, originalItems: any[]): any[] {
    switch (itemType) {
      case 'languages':
        return (this.fullUserProfile?.languages || []).filter((item) => item.isAiParsed && !item.id);
      case 'skills':
        return (this.fullUserProfile?.skills || []).filter((item) => item.isAiParsed && !item.id);
      case 'highSchool':
        return (this.fullUserProfile?.highSchoolEducations || []).filter((item) => item.isAiParsed && !item.id);
      case 'university':
        return (this.fullUserProfile?.universityEducations || []).filter((item) => item.isAiParsed && !item.id);
      case 'workExperience':
        // Enhanced filtering for work experiences to prevent duplicates
        const workExperiences = (this.fullUserProfile?.workExperiences || []).filter((item) => item.isAiParsed && !item.id);
        return this.removeDuplicateWorkExperiences(workExperiences);
      case 'projects':
        return (this.fullUserProfile?.projects || []).filter((item) => item.isAiParsed && !item.id);
      default:
        return [];
    }
  }

  /**
   * Remove duplicate work experiences based on employer, position, and dates
   */
  private removeDuplicateWorkExperiences(workExperiences: any[]): any[] {
    const uniqueExperiences: any[] = [];
    const seenExperiences = new Set<string>();

    for (const experience of workExperiences) {
      // Create a unique key based on key fields
      const key = `${experience.employer?.toLowerCase()}_${experience.position?.toLowerCase()}_${experience.startDate}_${experience.endDate}_${
        experience.current
      }`;

      if (!seenExperiences.has(key)) {
        seenExperiences.add(key);
        uniqueExperiences.push(experience);
      } else {
        console.log(`ProfileForm: Duplicate work experience detected and removed:`, experience);
      }
    }

    return uniqueExperiences;
  }

  /**
   * Remove duplicates from parsed data before loading into UI
   */
  private removeDuplicatesFromParsedData(items: any[], itemType: string): any[] {
    if (itemType === 'workExperience') {
      return this.removeDuplicateWorkExperiences(items);
    }

    // For other types, implement basic duplicate removal
    const uniqueItems: any[] = [];
    const seenItems = new Set<string>();

    for (const item of items) {
      let key = '';

      switch (itemType) {
        case 'skills':
          key = `${item.skillName?.toLowerCase()}_${item.level?.toLowerCase()}`;
          break;
        case 'languages':
          key = `${item.language?.toLowerCase()}_${item.level?.toLowerCase()}`;
          break;
        case 'projects':
          key = `${item.name?.toLowerCase()}_${item.description?.toLowerCase()}`;
          break;
        case 'university':
          key = `${item.universityName?.toLowerCase()}_${item.fieldOfStudy?.toLowerCase()}`;
          break;
        case 'highSchool':
          key = `${item.name?.toLowerCase()}_${item.country?.toLowerCase()}_${item.city?.toLowerCase()}`;
          break;
        default:
          key = JSON.stringify(item);
      }

      if (!seenItems.has(key)) {
        seenItems.add(key);
        uniqueItems.push(item);
      } else {
        console.log(`ProfileForm: Duplicate ${itemType} detected and removed:`, item);
      }
    }

    return uniqueItems;
  }

  /**
   * Check if a value is a placeholder or invalid
   */
  private isPlaceholderValue(value: string): boolean {
    if (!value || typeof value !== 'string') return true;

    const trimmedValue = value.trim().toLowerCase();

    // Common placeholder patterns
    const placeholderPatterns = [
      /^<.*>$/, // <placeholder>
      /^n\/a$/, // n/a
      /^not applicable$/, // not applicable
      /^unknown$/, // unknown
      /^none$/, // none
      /^null$/, // null
      /^undefined$/, // undefined
      /^\[.*\]$/, // [placeholder]
      /^placeholder$/, // placeholder
      /^example$/, // example
      /^sample$/, // sample
      /^test$/, // test
      /^demo$/, // demo
      /^lorem ipsum/, // lorem ipsum
    ];

    return placeholderPatterns.some((pattern) => pattern.test(trimmedValue));
  }

  /**
   * Load languages from parsed data
   */
  private loadLanguagesFromParsedData(languagesData: any[]): void {
    if (!this.userProfile) return;

    // Clear all existing languages before adding new parsed data
    this.userProfile.languages = [];

    // Define allowed language levels (uppercase for comparison)
    const allowedLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

    // Add each language from the parsed data, but only if it has a valid level
    languagesData.forEach((lang: any) => {
      // Skip languages with invalid levels
      if (!lang.level || !allowedLevels.includes(lang.level.toUpperCase())) {
        console.log(`ProfileForm: Skipping language "${lang.language}" with invalid level "${lang.level}". Allowed levels: ${allowedLevels.join(', ')}`);
        return;
      }

      const language: LanguageExtended = {
        language: lang.language || '',
        level: lang.level.toUpperCase(), // Normalize to uppercase
        edit: false,
        isAiParsed: true,
      };

      this.userProfile!.languages = this.userProfile!.languages || [];
      this.userProfile!.languages.push(language);
    });

    // Update fullUserProfile to reflect the changes in the UI
    if (this.fullUserProfile) {
      this.fullUserProfile.languages = [...this.userProfile.languages];
    }
  }

  /**
   * Load skills from parsed data
   */
  private loadSkillsFromParsedData(skillsData: any[]): void {
    if (!this.userProfile) {
      return;
    }

    // Clear all existing skills before adding new parsed data
    this.userProfile.skills = [];

    // Add each skill from the parsed data
    skillsData.forEach((sk: any) => {
      const skill: SkillExtended = {
        skillName: sk.skillName || '',
        level: sk.level || '',
        edit: false,
        isAiParsed: true, // Mark as AI-parsed regardless of input format
      };

      this.userProfile!.skills = this.userProfile!.skills || [];
      this.userProfile!.skills.push(skill);
    });

    // Update fullUserProfile to reflect the changes in the UI
    if (this.fullUserProfile) {
      this.fullUserProfile.skills = [...this.userProfile.skills];
    }
  }

  /**
   * Load drivers licenses from parsed data
   */
  private loadDriversLicensesFromParsedData(licensesData: string[]): void {
    if (!this.fullUserProfile) return;

    // driversLicenses is already an array of valid license strings from CV parsing service
    const licensesJson = JSON.stringify(licensesData);

    // Update fullUserProfile with parsed drivers licenses
    if (!this.fullUserProfile.driversLicenses) {
      this.fullUserProfile.driversLicenses = {} as DriversLicenses;
    }
    this.fullUserProfile.driversLicenses.driversLicenses = licensesJson;

    // Also update userProfile if it exists
    if (this.userProfile) {
      if (!this.userProfile.driversLicenses) {
        this.userProfile.driversLicenses = {} as DriversLicenses;
      }
      this.userProfile.driversLicenses.driversLicenses = licensesJson;
    }
  }

  /**
   * Load work experiences from parsed data
   */
  private loadWorkExperiencesFromParsedData(workExperiencesData: any[]): void {
    if (!this.userProfile) return;

    // Clear all existing work experiences before adding new parsed data
    this.userProfile.workExperiences = [];

    // Add each work experience from the parsed data
    workExperiencesData.forEach((work: any) => {
      const workExperience: WorkExperienceExtended = {
        employer: work.employer || '',
        position: work.position || '',
        city: work.city || '',
        country: work.country || '',
        description: work.description || '', // Add description field from AI parsing
        startDate: work.startDate || '',
        endDate: work.endDate || '',
        current: work.current || work.currentWork || false,
        edit: false,
        isAiParsed: true, // Mark as AI-parsed regardless of input format
      };

      this.userProfile!.workExperiences = this.userProfile!.workExperiences || [];
      this.userProfile!.workExperiences.push(workExperience);
    });

    // Update fullUserProfile to reflect the changes in the UI
    if (this.fullUserProfile) {
      this.fullUserProfile.workExperiences = [...this.userProfile.workExperiences];
    }
  }

  /**
   * Load projects from parsed data
   */
  private loadProjectsFromParsedData(projectsData: any[]): void {
    if (!this.userProfile) return;

    // Clear all existing projects before adding new parsed data
    this.userProfile.projects = [];

    // Add each project from the parsed data
    projectsData.forEach((proj: any) => {
      const project: ProjectExtended = {
        title: proj.projectName || proj.title || '',
        position: proj.position || '',
        duration: proj.duration ? parseInt(proj.duration.toString(), 10) : 0,
        country: proj.country || '',
        edit: false,
        isAiParsed: true,
      };

      this.userProfile!.projects = this.userProfile!.projects || [];
      this.userProfile!.projects.push(project);
    });

    // Update fullUserProfile to reflect the changes in the UI
    if (this.fullUserProfile) {
      this.fullUserProfile.projects = [...this.userProfile.projects];
    }
  }

  /**
   * Load university education from parsed data
   */
  /**
   * Resolve an education level key string (e.g. "masters_degree") to its numeric ID
   * using the cvOptions.educationLevels lookup table.
   */
  private resolveEducationLevelID(educationLevelKey: string): number | null {
    if (!educationLevelKey || !this.cvOptions?.educationLevels) return null;
    const match = this.cvOptions.educationLevels.find(
      (level: any) => level.educationKey === educationLevelKey
    );
    return match ? match.id : null;
  }

  private loadUniversityEducationFromParsedData(universityData: any[]): void {
    if (!this.userProfile) return;

    // Clear all existing university educations before adding new parsed data
    this.userProfile.universityEducations = [];

    // Add each university from the parsed data
    universityData.forEach((uni: any) => {
      const university: UniversityEducationExtended = {
        universityName: uni.universityName || '',
        fieldOfStudy: uni.fieldOfStudy || '',
        educationLevelID: uni.educationLevel ? this.resolveEducationLevelID(uni.educationLevel) || undefined : undefined,
        city: uni.city || '',
        country: uni.country || '',
        startDate: uni.startDate || '',
        endDate: uni.endDate || '',
        current: uni.current || false,
        edit: false,
        isAiParsed: true,
      };

      this.userProfile!.universityEducations = this.userProfile!.universityEducations || [];
      this.userProfile!.universityEducations.push(university);
    });

    // Update fullUserProfile to reflect the changes in the UI
    if (this.fullUserProfile) {
      this.fullUserProfile.universityEducations = [...this.userProfile.universityEducations];
    }
  }

  /**
   * Load high school education from parsed data
   */
  private loadHighSchoolEducationFromParsedData(highSchoolData: any[]): void {
    if (!this.userProfile) return;

    // Clear all existing high school educations before adding new parsed data
    this.userProfile.highSchoolEducations = [];

    // Add each high school from the parsed data
    highSchoolData.forEach((hs: any) => {
      const highSchool: HighSchoolEducationExtended = {
        highSchoolName: hs.name || hs.highSchoolName || '',
        fieldOfStudy: hs.fieldOfStudy || '',
        educationLevelID: hs.educationLevel ? this.resolveEducationLevelID(hs.educationLevel) || undefined : undefined,
        city: hs.city || '',
        country: hs.country || '',
        startDate: hs.startDate || '',
        endDate: hs.endDate || '',
        edit: false,
        isAiParsed: true,
      };

      this.userProfile!.highSchoolEducations = this.userProfile!.highSchoolEducations || [];
      this.userProfile!.highSchoolEducations.push(highSchool);
    });

    // Update fullUserProfile to reflect the changes in the UI
    if (this.fullUserProfile) {
      this.fullUserProfile.highSchoolEducations = [...this.userProfile.highSchoolEducations];
    }
  }

  /**
   * Determine if profile sections should be shown based on completion method and AI parsing state
   */
  shouldShowProfileSections(): boolean {
    // Show if manual method is selected OR if AI method with successful parsing OR if sections are explicitly set to show
    return (
      this.profileCompletionMethod === 'manual' ||
      (this.profileCompletionMethod === 'ai' && this.isParsingComplete && this.parsingSuccess) ||
      this.showProfileSectionsForMethod ||
      this.longCVHasAtLeastOneSection // Show if user already has data
    );
  }

  /**
   * Check if autosave has already been performed to prevent duplicate saves
   */
  private hasAlreadyAutosaved(): boolean {
    // Check if we've already performed autosave this session
    if (this.hasPerformedAutosave) {
      return true;
    }

    // Check if there are any AI-parsed items that already have IDs (indicating they've been saved)
    if (this.userProfile) {
      const hasLanguagesWithIds = this.userProfile.languages?.some((lang: LanguageExtended) => lang.isAiParsed && lang.id);
      const hasSkillsWithIds = this.userProfile.skills?.some((skill: SkillExtended) => skill.isAiParsed && skill.id);
      const hasWorkExpWithIds = this.userProfile.workExperiences?.some((work: WorkExperienceExtended) => work.isAiParsed && work.id);
      const hasProjectsWithIds = this.userProfile.projects?.some((project: ProjectExtended) => project.isAiParsed && project.id);
      const hasUniversityWithIds = this.userProfile.universityEducations?.some((uni: UniversityEducationExtended) => uni.isAiParsed && uni.id);
      const hasHighSchoolWithIds = this.userProfile.highSchoolEducations?.some((hs: HighSchoolEducationExtended) => hs.isAiParsed && hs.id);

      if (hasLanguagesWithIds || hasSkillsWithIds || hasWorkExpWithIds || hasProjectsWithIds || hasUniversityWithIds || hasHighSchoolWithIds) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if this is a recent parsing session (within the last 15 seconds)
   * This prevents autosave from triggering on page reloads/refreshes
   */
  private isRecentParsingSession(): boolean {
    const parsingTimestamp = localStorage.getItem('cvParsingTimestamp');
    if (!parsingTimestamp) {
      console.log('ProfileForm: No parsing timestamp found, not a recent parsing session');
      return false;
    }

    const timeSinceParsingMs = Date.now() - parseInt(parsingTimestamp, 10);
    const fifteenSecondsInMs = 15 * 1000; // Reduced from 30 to 15 seconds

    const isRecent = timeSinceParsingMs <= fifteenSecondsInMs;
    return isRecent;
  }

  /**
   * Get the current form data for job applications
   * This collects all the data from the form, whether it's saved or not
   */
  get currentFormData(): FullUserProfile | null {
    // If we don't have a userProfile initialized, return null
    if (!this.userProfile && !this.personalInfoForm) {
      return null;
    }

    // Build the profile data from current form state and userProfile
    // Get address values from form
    const streetName = this.personalInfoForm?.get('streetName')?.value || '';
    const streetNumber = this.personalInfoForm?.get('streetNumber')?.value || '';
    const city = this.personalInfoForm?.get('city')?.value || '';
    const postalCode = this.personalInfoForm?.get('zipCode')?.value || '';
    const country = this.personalInfoForm?.get('country')?.value || '';

    // Use selectedAddress stored in parent (synced from child via addressChange event)
    // This ensures we have the address even if child component is destroyed
    const selectedAddress = this.selectedAddress;

    // Build formattedAddress from form fields if not available from fullUserProfile
    const formattedAddress =
      this.fullUserProfile?.address?.formattedAddress ||
      selectedAddress?.formattedAddress ||
      `${streetName} ${streetNumber}, ${city} ${postalCode}, ${country}`.trim();

    const formData: FullUserProfile = {
      // Personal info from form
      firstName: this.personalInfoForm?.get('firstName')?.value || '',
      lastName: this.personalInfoForm?.get('lastName')?.value || '',
      gender: this.personalInfoForm?.get('gender')?.value || '',
      email: this.userEmail || '', // Use email passed from parent
      telephone: this.normalizePhoneValue(this.personalInfoForm?.get('phone')?.value, this.personalInfoForm?.get('phoneCountry')?.value),
      birthDate: this.personalInfoForm?.get('dateOfBirth')?.value || '',
      address: {
        streetName,
        streetNumber,
        city,
        postalCode,
        country,
        formattedAddress,
        latitude: selectedAddress?.latitude || this.fullUserProfile?.address?.latitude || 0,
        longitude: selectedAddress?.longitude || this.fullUserProfile?.address?.longitude || 0,
        administrativeArea: selectedAddress?.administrativeArea || this.fullUserProfile?.address?.administrativeArea || '',
      },

      // Profile sections - prefer fullUserProfile (where items are added), fallback to userProfile
      languages: this.fullUserProfile?.languages || this.userProfile?.languages || [],
      skills: this.fullUserProfile?.skills || this.userProfile?.skills || [],
      workExperiences: this.fullUserProfile?.workExperiences || this.userProfile?.workExperiences || [],
      projects: this.fullUserProfile?.projects || this.userProfile?.projects || [],
      universityEducations: this.fullUserProfile?.universityEducations || this.userProfile?.universityEducations || [],
      highSchoolEducations: this.fullUserProfile?.highSchoolEducations || this.userProfile?.highSchoolEducations || [],
      driversLicenses: this.fullUserProfile?.driversLicenses || this.userProfile?.driversLicenses || undefined,

      // Additional fields - get from form first, then fallback to fullUserProfile
      id: this.fullUserProfile?.id,
      title: this.personalInfoForm?.get('title')?.value || this.fullUserProfile?.title || '',
      titleAfterName: this.personalInfoForm?.get('titleAfterName')?.value || this.fullUserProfile?.titleAfterName || '',
      language: this.fullUserProfile?.language || '',
      aiMatching: this.personalInfoForm?.get('aiMatching')?.value ?? this.fullUserProfile?.aiMatching ?? false,
      aiParsing: this.personalInfoForm?.get('aiParsing')?.value ?? this.fullUserProfile?.aiParsing ?? false,
    };

    return formData;
  }

  /**
   * Handle drivers license changes from the drivers license section
   */
  onDriversLicenseChange(event: { action: 'save'; licenses: string[] }): void {
    if (!this.fullUserProfile) return;

    const { licenses } = event;
    const licensesJson = JSON.stringify(licenses);

    // Update the local state
    if (!this.fullUserProfile.driversLicenses) {
      this.fullUserProfile.driversLicenses = {} as DriversLicenses;
    }
    this.fullUserProfile.driversLicenses.driversLicenses = licensesJson;

    // Call the API to save
    this.userProfileService.updateDriversLicenses(licensesJson).subscribe({
      next: () => {
        this.toastService.success(
          this.translocoService.translate('profile.toast.updateSuccess'),
          this.translocoService.translate('profile.driversLicense.updated')
        );
        this.profileDataChange.emit();
      },
      error: (err: Error) => {
        console.error('Error updating drivers licenses:', err);
        this.toastService.error(this.translocoService.translate('shared.error'), this.translocoService.translate('profile.driversLicense.updateError'));
      },
    });
  }

  /**
   * Autosave drivers licenses from parsed CV data
   */
  private autosaveDriversLicenses(licensesData: string[]): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!licensesData || licensesData.length === 0) {
        resolve(null);
        return;
      }

      const licensesJson = JSON.stringify(licensesData);

      // Update local state
      if (this.fullUserProfile) {
        if (!this.fullUserProfile.driversLicenses) {
          this.fullUserProfile.driversLicenses = {} as DriversLicenses;
        }
        this.fullUserProfile.driversLicenses.driversLicenses = licensesJson;
      }

      // Call the API to save
      this.userProfileService.updateDriversLicenses(licensesJson).subscribe({
        next: (response: any) => {
          resolve(response);
        },
        error: (error: Error) => {
          console.error('ProfileForm: Error autosaving drivers licenses:', error);
          reject(error);
        },
      });
    });
  }
}
